import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import config from '../config';
import { api } from '../services/api';
import { auth } from '../services/auth';
import AlertDetailsDialog from './AlertDetailsDialog';
import SimplePolicySetup from './SimplePolicySetup';
import { NEUTRAL, STATUS, SEVERITY_COLORS, CARD_SHADOW, statusTint, textFor } from '../theme';

const toIso = (date) => (date ? new Date(date).toISOString() : null);

// For <input type="datetime-local"> we must provide LOCAL time (YYYY-MM-DDTHH:mm:ss).
// Include seconds so refresh-to-now is visible even within the same minute.
function toLocalDatetimeLocalValue(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const pad2 = (n) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mi = pad2(d.getMinutes());
  const ss = pad2(d.getSeconds());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
}

// Parse a datetime-local string as LOCAL time.
function fromLocalDatetimeLocalValue(value) {
  const v = String(value || '').trim();
  if (!v) return null;
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  const m2 = v.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
  const match = m2 || m;
  if (!match) return null;
  const [, y, mo, d, h, mi, ss] = match;
  const dt = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    ss ? Number(ss) : 0,
    0
  );
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function defaultStartTime() {
  const d = new Date();
  d.setHours(d.getHours() - 24);
  return d;
}

const formatTs = (ts) => {
  if (!ts) return '-';
  const seconds = ts._seconds ?? ts.seconds;
  if (typeof seconds === 'number') return new Date(seconds * 1000).toLocaleString('en-US', { hour12: false });
  if (typeof ts === 'string') return ts;
  return '-';
};

const formatDuration = (startedAt, endedAt) => {
  if (!startedAt || !endedAt) return '-';
  const startSeconds = startedAt._seconds ?? startedAt.seconds ?? 0;
  const endSeconds = endedAt._seconds ?? endedAt.seconds ?? 0;
  if (!startSeconds || !endSeconds) return '-';
  
  const durationSeconds = endSeconds - startSeconds;
  const hours = Math.floor(durationSeconds / 3600);
  const minutes = Math.floor((durationSeconds % 3600) / 60);
  const seconds = durationSeconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

const getNetworkManagerTemplateQuery = (query, owner) => {
  if (!query || owner === 'project') return query;
  let updated = String(query);
  updated = updated.replace(/source_project_id=~"PROJECT_PATTERN"/g, 'environment=~"PROJECT_PATTERN"');
  return updated;
};

// Component display names (stable across renders)
const COMPONENT_DISPLAY_NAMES = {
  pubsub: 'Pub/Sub',
  dataflow: 'Dataflow',
  dataform: 'Dataform',
  firestore: 'Firestore',
  cloud_functions: 'Cloud Functions',
  gke: 'GKE (Kubernetes)',
  edge_devices: 'Edge Devices',
  network_manager: 'Network Manager',
  lms: 'LMS',
  other: 'Other',
};

const ENV_SECTION_KEY = 'env-section';
const PROJECT_SECTION_KEY = 'project-section';

const SECTION_ROW_SX = { bgcolor: NEUTRAL[100], cursor: 'pointer', '&:hover': { bgcolor: NEUTRAL[200] } };
const SECTION_CELL_SX = { fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.02em', textTransform: 'uppercase', py: 1.25 };
const SECTION_META_SX = { fontWeight: 400, textTransform: 'none', letterSpacing: 0 };
const GROUP_ROW_SX = { bgcolor: 'background.paper', cursor: 'pointer', '&:hover': { bgcolor: NEUTRAL[50] } };
const GROUP_CELL_SX = { fontWeight: 600, py: 1 };

const POLICY_CATEGORIES = [
  { key: 'nms', label: 'NMS', Icon: RouterOutlinedIcon, components: ['network_manager'] },
  { key: 'lms', label: 'LMS', Icon: LightbulbOutlinedIcon, components: ['lms'] },
  { key: 'data', label: 'Data', Icon: StorageOutlinedIcon, components: ['pubsub', 'dataflow', 'dataform', 'edge_devices'] },
];
const OTHER_CATEGORY = { key: 'other', label: 'Other', Icon: CategoryOutlinedIcon };

// componentGroups: { [component]: policy[] } -> badge summaries in a fixed order.
const summarizePolicyCategories = (componentGroups) => {
  const totals = {};
  Object.entries(componentGroups).forEach(([component, list]) => {
    const cat = POLICY_CATEGORIES.find((c) => c.components.includes(component)) || OTHER_CATEGORY;
    if (!totals[cat.key]) totals[cat.key] = { ...cat, total: 0, active: 0 };
    totals[cat.key].total += list.length;
    totals[cat.key].active += list.filter((p) => p.enabled).length;
  });
  return [...POLICY_CATEGORIES, OTHER_CATEGORY].map((c) => totals[c.key]).filter(Boolean);
};

const withoutDisabled = (componentGroups) => {
  const next = {};
  Object.entries(componentGroups || {}).forEach(([component, list]) => {
    const enabled = list.filter((p) => p.enabled);
    if (enabled.length) next[component] = enabled;
  });
  return next;
};

const countDisabled = (componentGroups) =>
  Object.values(componentGroups || {}).reduce(
    (sum, list) => sum + list.filter((p) => !p.enabled).length,
    0
  );

function PolicyColumnsHeader({ columnWidths, onResize }) {
  const resizeHandle = (key) => (
    <Box
      sx={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: 8,
        cursor: 'col-resize',
        '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onResize(key, e.clientX, columnWidths[key]);
      }}
    />
  );
  const headCellSx = { position: 'relative', pr: 2, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' };
  return (
    <TableRow sx={{ bgcolor: 'action.hover' }}>
      <TableCell sx={headCellSx}>
        Display Name
        {resizeHandle('displayName')}
      </TableCell>
      <TableCell sx={headCellSx}>
        Enabled
        {resizeHandle('enabled')}
      </TableCell>
      <TableCell sx={headCellSx}>
        Conditions
        {resizeHandle('conditions')}
      </TableCell>
      <TableCell sx={headCellSx}>
        Subscription
        {resizeHandle('subscription')}
      </TableCell>
      <TableCell align="right" sx={headCellSx}>Actions</TableCell>
    </TableRow>
  );
}

const ENV_MARKER_IDS = new Set([
  'dev-juganu-smart-platform',
  'staging-juganu-smart-platform',
  'prod-juganu-smart-platform',
]);

const collectEnvironmentProjectIds = (networkData, lmsData) => {
  const ids = new Set();
  const add = (id) => {
    const pid = String(id || '').trim();
    if (!pid || ENV_MARKER_IDS.has(pid)) return;
    ids.add(pid);
  };
  const networkProjects = networkData?.projects;
  if (networkProjects && typeof networkProjects === 'object' && !Array.isArray(networkProjects)) {
    Object.entries(networkProjects).forEach(([key, project]) => {
      add(project?.project_id || key);
    });
  }
  const lmsProjects = lmsData?.projects;
  if (Array.isArray(lmsProjects)) {
    lmsProjects.forEach((project) => add(project?.project_id));
  }
  return Array.from(ids).sort((a, b) => a.localeCompare(b));
};

const MonitoringDashboard = ({ demoMode }) => {
  const env = useMemo(() => config.getEnvironment(), []);
  const theme = useTheme();
  const [tab, setTab] = useState(0); // 0 = Alerts, 1 = Policies

  const [startTime, setStartTime] = useState(() => defaultStartTime());
  const [endTime, setEndTime] = useState(() => new Date());

  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState(null);
  const [alertsData, setAlertsData] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [policiesLoading, setPoliciesLoading] = useState(false);
  const alertsInFlightRef = useRef(false);
  const policiesInFlightRef = useRef(false);
  const policiesRefetchQueuedRef = useRef(false);
  const fetchPoliciesRef = useRef(null);
  // Policies created from the simple form, kept until the server list includes them.
  const pendingCreatedPoliciesRef = useRef(new Map());
  const [policiesError, setPoliciesError] = useState(null);
  const [policiesData, setPoliciesData] = useState(null);
  const [updatingPolicyId, setUpdatingPolicyId] = useState(null);
  const [disablingGroupKey, setDisablingGroupKey] = useState(null);
  const [deletingGroupKey, setDeletingGroupKey] = useState(null);
  const [deletingPolicyId, setDeletingPolicyId] = useState(null);
  const [subscribingPolicy, setSubscribingPolicy] = useState(null);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribeError, setSubscribeError] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [unsubscribingKey, setUnsubscribingKey] = useState(null);
  const [alertSearch, setAlertSearch] = useState('');
  const [policySearch, setPolicySearch] = useState('');
  const [alertFilter, setAlertFilter] = useState('all'); // 'all', 'open', 'closed'
  const [alertComponentFilter, setAlertComponentFilter] = useState('all');
  const [componentFilter, setComponentFilter] = useState('all'); // Filter by component label
  const [policyView, setPolicyView] = useState('simple'); // 'simple' | 'advanced'
  const [showDisabled, setShowDisabled] = useState(false);
  const [environmentProjectIds, setEnvironmentProjectIds] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({}); // Track which component groups are expanded
  const [selectedAlertIds, setSelectedAlertIds] = useState(new Set());
  const [deletingAlerts, setDeletingAlerts] = useState(false);
  
  // Create policy dialog state
  const [createPolicyOpen, setCreatePolicyOpen] = useState(false);
  const [createMode, setCreateMode] = useState('simple'); // 'simple' | 'advanced'
  const [createBusy, setCreateBusy] = useState(false);
  const [editPolicyOpen, setEditPolicyOpen] = useState(false);
  const [createChannelsMenuOpen, setCreateChannelsMenuOpen] = useState(false);
  const [editChannelsMenuOpen, setEditChannelsMenuOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [editPolicyForm, setEditPolicyForm] = useState({
    component: '',
    displayName: '',
    threshold: '',
    duration: '',
    evaluationInterval: '',
    projectId: '',
    owner: 'system',
    customQuery: '',
    autoClose: '7d',
    selectedChannels: [],
    extraChannels: [],
    conditionName: '',
    newEmailChannel: '',
  });
  const [updatingPolicy, setUpdatingPolicy] = useState(false);
  const [editPolicyError, setEditPolicyError] = useState(null);
  const [policyTemplates, setPolicyTemplates] = useState({});
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [notificationChannels, setNotificationChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [createPolicyForm, setCreatePolicyForm] = useState({
    component: '',
    templateId: '',
    templateParams: {},
    severity: 'warning',
    displayName: '',
    threshold: '',
    duration: '',
    evaluationInterval: '',
    projectPattern: '',
    projectId: '',
    owner: 'system',
    customQuery: '',
    useCustomQuery: false,
    autoClose: '7d',
    selectedChannels: [],
    newEmailChannel: '',
  });
  const [creatingPolicy, setCreatingPolicy] = useState(false);
  const [createPolicyError, setCreatePolicyError] = useState(null);
  const [addingEmailChannel, setAddingEmailChannel] = useState(false);
  
  // Guided tour (in-app help)
  const [tourOpen, setTourOpen] = useState(false);
  const [tourMode, setTourMode] = useState('lms'); // 'lms' | 'network_manager'
  const [tourStep, setTourStep] = useState(0);

  // Column widths for policy table (in percentages)
  const [columnWidths, setColumnWidths] = useState({
    displayName: 22,
    enabled: 8,
    conditions: 35,
    subscription: 28,
    actions: 7,
  });

  // Inject lightweight highlight styles once
  useEffect(() => {
    const id = 'monitoring-tour-styles';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      .tour-highlight {
        outline: 2px solid var(--pm-n900);
        outline-offset: 3px;
        border-radius: 6px;
        box-shadow: 0 0 0 4px var(--pm-n100);
        transition: box-shadow 180ms ease, outline 180ms ease;
      }
    `;
    document.head.appendChild(style);
  }, []);

  const focusTourEl = useCallback((id) => {
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('tour-highlight');
      window.setTimeout(() => el.classList.remove('tour-highlight'), 1800);
    } catch (_e) {
      // ignore
    }
  }, []);

  const startTour = useCallback((mode) => {
    setTourMode(mode);
    setTourStep(0);
    setTourOpen(true);
    // Start from Policies tab
    setTab(1);
    // Close dialogs so tour controls the flow
    setCreatePolicyOpen(false);
    setEditPolicyOpen(false);
  }, []);

  // Register demo mode controls
  useEffect(() => {
    if (!demoMode) return;
    
    const openFirstMonitoringAlert = () => {
      const alerts = Array.isArray(alertsData?.events) ? alertsData.events : [];
      if (alerts.length > 0) {
        setSelectedAlert(alerts[0]);
        setDetailsOpen(true);
      }
    };

    const closeMonitoringAlertDetails = () => {
      setDetailsOpen(false);
      setSelectedAlert(null);
    };

    demoMode.registerControls('monitoring', {
      openFirstMonitoringAlert,
      closeMonitoringAlertDetails,
    });
  }, [demoMode, alertsData]);

  const fetchAlerts = useCallback(async ({ startTimeOverride, endTimeOverride, signal } = {}) => {
    if (alertsInFlightRef.current) return;
    try {
      alertsInFlightRef.current = true;
      setAlertsLoading(true);
      setAlertsError(null);
      const allowedProjects = auth.getAllowedProjectIds();
      const markers = new Set(
        allowedProjects.filter((p) =>
          ['dev-juganu-smart-platform', 'staging-juganu-smart-platform', 'prod-juganu-smart-platform'].includes(p)
        )
      );
      let data;
      const st = startTimeOverride || startTime;
      const et = endTimeOverride || endTime;
      if (markers.size === 1) {
        data = await api.getEnvAlerts({
          startTime: toIso(st),
          endTime: toIso(et),
          limit: 200,
          signal,
        });
      } else if (markers.size === 0) {
        data = await api.getProjectsAlerts({
          startTime: toIso(st),
          endTime: toIso(et),
          limit: 200,
          includeAllowedProjects: true,
          signal,
        });
      } else {
        throw new Error(`Multiple env markers in allowed projects: ${Array.from(markers).join(', ')}`);
      }
      setAlertsData(data);
    } catch (e) {
      if (!e?.isAbort) {
        setAlertsError(e.message || 'Failed to load alerts');
      }
    } finally {
      setAlertsLoading(false);
      alertsInFlightRef.current = false;
    }
  }, [startTime, endTime]);

  const refreshAlertsToNow = useCallback(() => {
    const now = new Date();
    setEndTime(now);
    fetchAlerts({ endTimeOverride: now });
  }, [fetchAlerts]);

  const fetchPolicies = useCallback(async ({ signal } = {}) => {
    if (policiesInFlightRef.current) {
      policiesRefetchQueuedRef.current = true;
      return;
    }
    try {
      policiesInFlightRef.current = true;
      setPoliciesLoading(true);
      setPoliciesError(null);
      let data = await api.getEnvPolicies({ includeDisabled: true, signal });
      const pending = pendingCreatedPoliciesRef.current;
      if (pending.size > 0 && Array.isArray(data?.policies)) {
        data.policies.forEach((p) => pending.delete(p.name));
        data = { ...data, policies: [...data.policies, ...pending.values()] };
      }
      setPoliciesData(data);
    } catch (e) {
      if (!e?.isAbort) {
        setPoliciesError(e.message || 'Failed to load policies');
      }
    } finally {
      setPoliciesLoading(false);
      policiesInFlightRef.current = false;
      if (policiesRefetchQueuedRef.current) {
        policiesRefetchQueuedRef.current = false;
        window.setTimeout(() => fetchPoliciesRef.current?.(), 0);
      }
    }
  }, []);
  fetchPoliciesRef.current = fetchPolicies;

  const applySimplePolicyChanges = useCallback(({ created = [], enabled = [], subscribed = [] }) => {
    const enabledSet = new Set(enabled);
    const emailsByPolicy = {};
    subscribed.forEach(({ policyName, email }) => {
      (emailsByPolicy[policyName] = emailsByPolicy[policyName] || []).push(email);
    });
    created.forEach((p) => {
      if (p?.name) pendingCreatedPoliciesRef.current.set(p.name, { ...p, subscribers: p.subscribers || [] });
    });
    setPoliciesData((prev) => {
      if (!prev || !Array.isArray(prev.policies)) return prev;
      const list = prev.policies.map((p) => {
        let next = enabledSet.has(p.name) ? { ...p, enabled: true } : p;
        const adds = emailsByPolicy[p.name];
        if (adds) {
          const existing = Array.isArray(next.subscribers) ? next.subscribers : [];
          const fresh = adds
            .filter((email) => !existing.some((sub) => sub?.email === email))
            .map((email) => ({ email, channelName: null, displayName: email, type: 'email' }));
          next = { ...next, subscribers: [...existing, ...fresh] };
        }
        return next;
      });
      const known = new Set(list.map((p) => p.name));
      created.forEach((p) => {
        if (p?.name && !known.has(p.name)) list.push({ ...p, subscribers: p.subscribers || [] });
      });
      return { ...prev, policies: list };
    });
    fetchPolicies();
  }, [fetchPolicies]);

  // Fetch notification channels
  const fetchNotificationChannels = useCallback(async ({ signal } = {}) => {
    setChannelsLoading(true);
    try {
      const result = await api.getNotificationChannels({ signal });
      setNotificationChannels(result.channels || []);
    } catch (err) {
      if (!err?.isAbort) {
        console.error('Error fetching notification channels:', err);
      }
    } finally {
      setChannelsLoading(false);
    }
  }, []);

  const parseDurationInput = useCallback((value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;
    const match = raw.match(/^(\d+(?:\.\d+)?)\s*(s|sec|secs|second|seconds|m|min|mins|minute|minutes|h|hr|hrs|hour|hours|d|day|days)?$/i);
    if (!match) return null;
    const num = parseFloat(match[1]);
    if (!Number.isFinite(num) || num <= 0) return null;
    const unit = (match[2] || 's').toLowerCase();
    const multipliers = {
      s: 1,
      sec: 1,
      secs: 1,
      second: 1,
      seconds: 1,
      m: 60,
      min: 60,
      mins: 60,
      minute: 60,
      minutes: 60,
      h: 3600,
      hr: 3600,
      hrs: 3600,
      hour: 3600,
      hours: 3600,
      d: 86400,
      day: 86400,
      days: 86400,
    };
    const seconds = Math.round(num * (multipliers[unit] || 1));
    return `${seconds}s`;
  }, []);

  const handleTogglePolicy = useCallback(async (policy, newEnabled) => {
    // Extract policy ID from name: projects/{project}/alertPolicies/{policyId}
    const policyId = policy.name.split('/').pop();
    if (!policyId) {
      setPoliciesError('Invalid policy ID');
      return;
    }

    try {
      setUpdatingPolicyId(policyId);
      if (policy?.userLabels?.owner === 'project') {
        await api.updateProjectPolicyEnabled(policyId, newEnabled);
      } else {
        await api.updatePolicyEnabled(policyId, newEnabled);
      }
      setPoliciesData((prev) => {
        if (!prev || !Array.isArray(prev.policies)) return prev;
        return {
          ...prev,
          policies: prev.policies.map((p) => (p.name === policy.name ? { ...p, enabled: newEnabled } : p)),
        };
      });
    } catch (e) {
      setPoliciesError(e.message || 'Failed to update policy');
    } finally {
      setUpdatingPolicyId(null);
    }
  }, []);

  // Helper to convert duration/evaluationInterval from object to string
  const formatDurationFromCondition = useCallback((value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value.seconds !== undefined) {
      const seconds = Number(value.seconds);
      if (Number.isFinite(seconds) && seconds > 0) {
        // Convert to human-readable format
        if (seconds % 86400 === 0) return `${seconds / 86400}d`;
        if (seconds % 3600 === 0) return `${seconds / 3600}h`;
        if (seconds % 60 === 0) return `${seconds / 60}m`;
        return `${seconds}s`;
      }
    }
    return '';
  }, []);

  const handleOpenEditPolicy = useCallback((policy, condition) => {
    const firstCondition = condition || (policy.conditions || [])[0] || {};
    const channelIndex = new Map(notificationChannels.map((c) => [c.name, c]));
    const policyChannels = Array.isArray(policy.notificationChannels) ? policy.notificationChannels : [];
    const subscriberEmailChannels = (policy.subscribers || [])
      .filter((s) => s.email && s.channelName)
      .map((s) => s.channelName);
    const emailChannelNames = policyChannels.filter((name) => {
      const ch = channelIndex.get(name);
      return ch?.type === 'email' || ch?.email;
    });
    const selectedEmailChannels = emailChannelNames.length > 0 ? emailChannelNames : subscriberEmailChannels;
    const extraChannels = policyChannels.filter((name) => !selectedEmailChannels.includes(name));

    // Convert threshold to string (handle both string and number, including 0)
    // If backend didn't extract it, try to extract from query as fallback
    let thresholdValue = firstCondition.threshold;
    if ((thresholdValue === null || thresholdValue === undefined || thresholdValue === '') && firstCondition.query) {
      // Fallback: extract threshold from query if backend didn't provide it
      const query = firstCondition.query;
      let match = query.match(/\s*([<>=!]+)\s+(\d+\.?\d*|THRESHOLD)\s*$/);
      if (!match) {
        match = query.match(/\s*([<>=!]+)(\d+\.?\d*|THRESHOLD)\s*$/);
      }
      if (match && match[2]) {
        const value = match[2];
        if (value !== 'THRESHOLD' && !isNaN(Number(value))) {
          thresholdValue = value;
        }
      }
    }
    const thresholdString = thresholdValue !== null && thresholdValue !== undefined && thresholdValue !== ''
      ? String(thresholdValue)
      : '';

    const conditionType = firstCondition.type || (firstCondition.query ? 'promql' : firstCondition.filter ? 'threshold' : '');
    const queryOrFilter = firstCondition.query || firstCondition.filter || '';

    setEditingPolicy(policy);
    setEditPolicyForm({
      component: policy.userLabels?.component || '',
      displayName: policy.displayName || '',
      threshold: thresholdString,
      duration: formatDurationFromCondition(firstCondition.duration),
      evaluationInterval: formatDurationFromCondition(firstCondition.evaluationInterval) || '60s',
      projectId: policy.userLabels?.project_id || '',
      owner: policy.userLabels?.owner || (policy.userLabels?.project_id ? 'project' : 'system'),
      customQuery: queryOrFilter,
      conditionType,
      autoClose: policy.autoClose || '7d',
      selectedChannels: selectedEmailChannels,
      extraChannels,
      conditionName: firstCondition.name || '',
      newEmailChannel: '',
    });
    setEditPolicyError(null);
    setEditPolicyOpen(true);
    if (!notificationChannels.length) fetchNotificationChannels();
  }, [notificationChannels, fetchNotificationChannels, formatDurationFromCondition]);

  const handleEditPolicyFormChange = useCallback((field, value) => {
    setEditPolicyForm((prev) => {
      const updated = { ...prev, [field]: value };
      // When threshold changes, update the query to reflect the new threshold
      if (field === 'threshold' && prev.customQuery && prev.conditionType !== 'threshold') {
        const query = prev.customQuery;
        const nextValue = value === undefined || value === null ? '' : String(value).trim();
        if (nextValue) {
          // If the query contains THRESHOLD placeholders, replace them directly.
          if (query.includes('THRESHOLD')) {
            updated.customQuery = query.replace(/THRESHOLD/g, nextValue);
          } else {
            // Replace the comparison that matches the current threshold (fallback to first).
            const matcher = /([<>=!]+)\s*([0-9]+(?:\.[0-9]+)?)/g;
            const matches = [];
            let m;
            while ((m = matcher.exec(query)) !== null) {
              matches.push({
                index: m.index,
                length: m[0].length,
                op: m[1],
                value: m[2],
              });
            }
            if (matches.length > 0) {
              const prevNum = Number(prev.threshold);
              let target = matches[0];
              if (Number.isFinite(prevNum)) {
                const found = matches.find((entry) => Number(entry.value) === prevNum);
                if (found) target = found;
              }
              updated.customQuery =
                query.slice(0, target.index) +
                `${target.op} ${nextValue}` +
                query.slice(target.index + target.length);
            }
          }
        }
      }
      return updated;
    });
  }, []);

  const handleSavePolicyEdits = useCallback(async () => {
    if (!editingPolicy) return;
    if (!editPolicyForm.displayName) {
      setEditPolicyError('Display name is required');
      return;
    }
    if (!editPolicyForm.customQuery) {
      setEditPolicyError(editPolicyForm.conditionType === 'threshold' ? 'Filter is required' : 'Query is required');
      return;
    }
    if (!editPolicyForm.threshold) {
      setEditPolicyError('Threshold is required');
      return;
    }
    if (!editPolicyForm.duration) {
      setEditPolicyError('Duration is required');
      return;
    }
    if (editPolicyForm.conditionType !== 'threshold' && !editPolicyForm.evaluationInterval) {
      setEditPolicyError('Evaluation interval is required');
      return;
    }

    const policyId = editingPolicy.name.split('/').pop();
    if (!policyId) {
      setEditPolicyError('Invalid policy ID');
      return;
    }

    try {
      setUpdatingPolicy(true);
      setEditPolicyError(null);
      const durationSeconds = parseDurationInput(editPolicyForm.duration);
      const evalSeconds = parseDurationInput(editPolicyForm.evaluationInterval);
      // Note: autoClose cannot be updated after policy creation (read-only field)
      const notificationChannelNames = Array.from(
        new Set([...(editPolicyForm.extraChannels || []), ...(editPolicyForm.selectedChannels || [])])
      );

      // Always update policy-level metadata (display name + notification channels).
      const metaPayload = {
        displayName: editPolicyForm.displayName,
        // autoClose is read-only, cannot be updated
        notificationChannels: notificationChannelNames,
        conditionName: editPolicyForm.conditionName || undefined,
        ...(editPolicyForm.conditionType === 'threshold'
          ? {}
          : {
              query: editPolicyForm.customQuery,
              duration: durationSeconds || undefined,
              evaluationInterval: evalSeconds || undefined,
            }),
      };

      if (editingPolicy?.userLabels?.owner === 'project') {
        await api.updateProjectPolicy(policyId, metaPayload);
      } else {
        await api.updatePolicy(policyId, metaPayload);
      }

      // For filter/threshold-based conditions, update the condition_threshold fields.
      if (editPolicyForm.conditionType === 'threshold') {
        const thresholdNum = Number(editPolicyForm.threshold);
        const thresholdPayload = {
          filter: editPolicyForm.customQuery,
          threshold: Number.isFinite(thresholdNum) ? thresholdNum : editPolicyForm.threshold,
          // duration: intentionally omitted (Duration proto vs UI string); keep existing duration
        };
        if (editingPolicy?.userLabels?.owner === 'project') {
          await api.updateProjectPolicyConditionThreshold(policyId, editPolicyForm.conditionName, thresholdPayload);
        } else {
          await api.updatePolicyConditionThreshold(policyId, editPolicyForm.conditionName, thresholdPayload);
        }
      }

      const channelIndex = new Map(notificationChannels.map((c) => [c.name, c]));
      const updatedSubscribers = notificationChannelNames.map((name) => {
        const ch = channelIndex.get(name);
        const email = ch?.email || null;
        return {
          email,
          channelName: name,
          displayName: ch?.displayName || email || name.split('/').pop(),
          type: ch?.type || 'unknown',
        };
      });

      setPoliciesData((prev) => {
        if (!prev || !Array.isArray(prev.policies)) return prev;
        return {
          ...prev,
          policies: prev.policies.map((p) => {
            if (p.name !== editingPolicy.name) return p;
            const updatedConditions = (p.conditions || []).map((c) => {
              if (editPolicyForm.conditionName && c.name !== editPolicyForm.conditionName) return c;
              return {
                ...c,
                ...(editPolicyForm.conditionType === 'threshold'
                  ? { filter: editPolicyForm.customQuery }
                  : { query: editPolicyForm.customQuery }),
                threshold: editPolicyForm.threshold,
                duration: editPolicyForm.duration,
                evaluationInterval: editPolicyForm.evaluationInterval,
              };
            });
            return {
              ...p,
              displayName: editPolicyForm.displayName,
              notificationChannels: notificationChannelNames,
              subscribers: updatedSubscribers,
              // autoClose remains unchanged (read-only)
              conditions: updatedConditions,
            };
          }),
        };
      });

      setEditPolicyOpen(false);
      setEditingPolicy(null);
    } catch (e) {
      setEditPolicyError(e.message || 'Failed to update policy');
    } finally {
      setUpdatingPolicy(false);
    }
  }, [
    editingPolicy,
    editPolicyForm,
    notificationChannels,
    parseDurationInput,
  ]);

  const handleDeletePolicy = useCallback(async (policy) => {
    if (!window.confirm(`Are you sure you want to delete policy "${policy.displayName}"?`)) {
      return;
    }

    const policyId = policy.name.split('/').pop();
    if (!policyId) {
      setPoliciesError('Invalid policy ID');
      return;
    }

    try {
      setDeletingPolicyId(policyId);
      if (policy?.userLabels?.owner === 'project') {
        await api.deleteProjectPolicy(policyId);
      } else {
        await api.deletePolicy(policyId);
      }
      pendingCreatedPoliciesRef.current.delete(policy.name);
      setPoliciesData((prev) => {
        if (!prev || !Array.isArray(prev.policies)) return prev;
        return {
          ...prev,
          policies: prev.policies.filter((p) => p.name !== policy.name),
        };
      });
    } catch (e) {
      setPoliciesError(e.message || 'Failed to delete policy');
    } finally {
      setDeletingPolicyId(null);
    }
  }, []);

  const handleDeleteSelectedAlerts = useCallback(async () => {
    if (selectedAlertIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedAlertIds.size} selected alert(s)? This cannot be undone.`)) {
      return;
    }
    try {
      setDeletingAlerts(true);
      const ids = Array.from(selectedAlertIds);
      await api.deleteAlerts({ ids });
      setAlertsData((prev) => {
        if (!prev || !Array.isArray(prev.events)) return prev;
        return {
          ...prev,
          events: prev.events.filter((e) => !ids.includes(e.id)),
        };
      });
      setSelectedAlertIds(new Set());
    } catch (e) {
      setAlertsError(e.message || 'Failed to delete alerts');
    } finally {
      setDeletingAlerts(false);
    }
  }, [selectedAlertIds]);

  const handleDeleteComponentAlerts = useCallback(async () => {
    if (alertComponentFilter === 'all') return;
    if (
      !window.confirm(
        `Delete all "${COMPONENT_DISPLAY_NAMES[alertComponentFilter] || alertComponentFilter}" alerts in the selected time range? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      setDeletingAlerts(true);
      const payload = {
        component: alertComponentFilter,
        startTime: startTime?.toISOString?.(),
        endTime: endTime?.toISOString?.(),
        limit: 500,
        ...(alertFilter !== 'all' ? { state: alertFilter } : {}),
      };
      await api.deleteAlerts(payload);
      await fetchAlerts();
      setSelectedAlertIds(new Set());
    } catch (e) {
      setAlertsError(e.message || 'Failed to delete alerts');
    } finally {
      setDeletingAlerts(false);
    }
  }, [alertComponentFilter, startTime, endTime, alertFilter, fetchAlerts]);

  const handleSubscribeClick = useCallback((policy) => {
    setSubscribingPolicy(policy);
    setSubscribeEmail('');
    setSubscribeError(null);
  }, []);

  const handleSubscribe = useCallback(async () => {
    if (!subscribeEmail || !subscribeEmail.includes('@')) {
      setSubscribeError('Please enter a valid email address');
      return;
    }

    if (!subscribingPolicy) return;

    const policyId = subscribingPolicy.name.split('/').pop();
    if (!policyId) {
      setSubscribeError('Invalid policy ID');
      return;
    }

    try {
      setSubscribing(true);
      setSubscribeError(null);
      if (subscribingPolicy?.userLabels?.owner === 'project') {
        await api.subscribeToProjectPolicy(policyId, subscribeEmail);
      } else {
        await api.subscribeToPolicy(policyId, subscribeEmail);
      }
      const policyName = subscribingPolicy.name;
      const emailToAdd = subscribeEmail.trim().toLowerCase();
      setSubscribingPolicy(null);
      setSubscribeEmail('');
      setPoliciesData((prev) => {
        if (!prev || !Array.isArray(prev.policies)) return prev;
        return {
          ...prev,
          policies: prev.policies.map((p) => {
            if (p.name !== policyName) return p;
            const existing = Array.isArray(p.subscribers) ? p.subscribers : [];
            if (existing.some((s) => s?.email === emailToAdd)) return p;
            return {
              ...p,
              subscribers: [
                ...existing,
                {
                  email: emailToAdd,
                  channelName: null,
                  displayName: emailToAdd,
                  type: 'email',
                },
              ],
            };
          }),
        };
      });
    } catch (e) {
      setSubscribeError(e.message || 'Failed to subscribe to policy');
    } finally {
      setSubscribing(false);
    }
  }, [subscribeEmail, subscribingPolicy]);

  const handleUnsubscribe = useCallback(async (policy, email) => {
    const policyId = policy.name.split('/').pop();
    if (!policyId) {
      setPoliciesError('Invalid policy ID');
      return;
    }
    try {
      const key = `${policyId}:${email}`;
      setUnsubscribingKey(key);
      await api.unsubscribeFromPolicy(policyId, email);
      await fetchPolicies();
    } catch (e) {
      setPoliciesError(e.message || 'Failed to unsubscribe');
    } finally {
      setUnsubscribingKey(null);
    }
  }, [fetchPolicies]);

  useEffect(() => {
    // Load the active tab data on mount (do not auto-retry after errors)
    if (tab === 0 && !alertsData && !alertsError) {
      const controller = new AbortController();
      fetchAlerts({ signal: controller.signal });
      return () => controller.abort();
    }
    if (tab === 1 && !policiesData && !policiesError) {
      const controller = new AbortController();
      fetchPolicies({ signal: controller.signal });
      return () => controller.abort();
    }
    return undefined;
  }, [tab, alertsData, policiesData, alertsError, policiesError, fetchAlerts, fetchPolicies]);

  // Deduplicate alerts by incidentId - prefer closed state, then most recent receivedAt
  const alertsRaw = Array.isArray(alertsData?.events) ? alertsData.events : [];
  const policiesReadOnly = Boolean(policiesData?.readOnly);
  const canCreatePolicy = !policiesReadOnly || (policiesData?.readOnlyReason || '').includes('No env marker');
  const allowedProjectIds = Array.from(
    new Set([auth.getProjectId(), ...auth.getAllowedProjectIds()].filter(Boolean))
  );
  const envMarkers = allowedProjectIds.filter((id) =>
    id === 'dev-juganu-smart-platform' ||
    id === 'staging-juganu-smart-platform' ||
    id === 'prod-juganu-smart-platform'
  );
  const isEnvUser = envMarkers.length === 1;
  const scopedAllowedProjects = allowedProjectIds.filter((id) => !envMarkers.includes(id));
  const alertsMap = new Map();
  alertsRaw.forEach((a) => {
    const incidentId = a.incidentId;
    if (!incidentId) {
      // If no incidentId, use the document ID as fallback
      alertsMap.set(a.id, a);
      return;
    }
    const existing = alertsMap.get(incidentId);
    if (!existing) {
      alertsMap.set(incidentId, a);
    } else {
      // Prefer closed state over open state
      const existingIsClosed = existing.state === 'closed';
      const currentIsClosed = a.state === 'closed';
      
      if (currentIsClosed && !existingIsClosed) {
        // Current is closed, existing is open - prefer closed
        alertsMap.set(incidentId, a);
      } else if (!currentIsClosed && existingIsClosed) {
        // Current is open, existing is closed - keep existing (closed)
        // Don't replace
      } else {
        // Both have same state (both closed or both open) - keep most recent by receivedAt
        const existingReceived = existing.receivedAt?._seconds || existing.receivedAt?.seconds || 0;
        const currentReceived = a.receivedAt?._seconds || a.receivedAt?.seconds || 0;
        if (currentReceived > existingReceived) {
          alertsMap.set(incidentId, a);
        }
      }
    }
  });
  const alerts = Array.from(alertsMap.values());
  const availableAlertComponents = useMemo(() => {
    const set = new Set();
    alerts.forEach((a) => {
      if (a.component) set.add(a.component);
    });
    return Array.from(set).sort();
  }, [alerts]);
  const filteredAlerts = alerts.filter((a) => {
    // Apply state filter
    if (alertFilter === 'open' && a.state !== 'open') return false;
    if (alertFilter === 'closed' && a.state !== 'closed') return false;
    if (alertComponentFilter !== 'all' && a.component !== alertComponentFilter) return false;
    
    // Apply search filter
    if (!alertSearch) return true;
    const haystack = [
      a.projectId,
      a.policyName,
      a.conditionName,
      a.state,
      a.incidentId,
      a.component,
      a.severity,
      a.alertType,
      COMPONENT_DISPLAY_NAMES[a.component], // Also search by display name
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(alertSearch.toLowerCase());
  });

  const alertIdSet = useMemo(() => new Set(alerts.map((a) => a.id).filter(Boolean)), [alerts]);
  const filteredAlertIds = useMemo(
    () => filteredAlerts.map((a) => a.id).filter(Boolean),
    [filteredAlerts]
  );
  const allFilteredSelected =
    filteredAlertIds.length > 0 && filteredAlertIds.every((id) => selectedAlertIds.has(id));

  useEffect(() => {
    if (selectedAlertIds.size === 0) return;
    const next = new Set(Array.from(selectedAlertIds).filter((id) => alertIdSet.has(id)));
    if (next.size !== selectedAlertIds.size) {
      setSelectedAlertIds(next);
    }
  }, [alertIdSet, selectedAlertIds]);

  const toggleAlertSelection = useCallback((id, checked) => {
    if (!id) return;
    setSelectedAlertIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleSelectAllFilteredAlerts = useCallback((checked) => {
    setSelectedAlertIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        filteredAlertIds.forEach((id) => next.add(id));
      } else {
        filteredAlertIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  }, [filteredAlertIds]);
  
  const policies = useMemo(
    () => (Array.isArray(policiesData?.policies) ? policiesData.policies : []),
    [policiesData]
  );
  const emailNotificationChannels = useMemo(
    () => notificationChannels.filter((ch) => ch.type === 'email' || !!ch.email),
    [notificationChannels]
  );
  
  // Extract unique components from policies
  const availableComponents = useMemo(() => {
    const components = new Set();
    policies.forEach((p) => {
      const component = p.userLabels?.component;
      if (component) components.add(component);
    });
    return Array.from(components).sort();
  }, [policies]);
  
  const viewEnv = String(env || 'unknown').toLowerCase();

  const getPolicyEnv = useCallback((policy) => {
    const labelEnv = policy?.userLabels?.env;
    if (labelEnv) return String(labelEnv).toLowerCase();
    return 'unknown';
  }, []);

  const getPolicyOwner = useCallback((policy) => {
    const owner = policy?.userLabels?.owner;
    if (owner === 'system' || owner === 'project') return owner;
    const scope = policy?.userLabels?.scope;
    if (scope === 'project') return 'project';
    if (scope === 'env') return 'system';
    if (policy?.userLabels?.project_id) return 'project';
    const envGuess = getPolicyEnv(policy);
    return envGuess !== 'unknown' ? 'system' : 'project';
  }, [getPolicyEnv]);

  const handleSetGroupEnabled = useCallback(async (groupKey, policiesInGroup, enabled) => {
    const candidates = (policiesInGroup || []).filter((p) => Boolean(p.enabled) !== enabled);
    const targets = candidates.filter((p) => getPolicyOwner(p) !== 'system' || (isEnvUser && !policiesReadOnly));
    if (targets.length === 0 || disablingGroupKey) return;
    const component = targets[0]?.userLabels?.component;
    const label = COMPONENT_DISPLAY_NAMES[component] || component || 'this category';
    const verb = enabled ? 'Enable' : 'Disable';
    const envCount = targets.filter((p) => getPolicyOwner(p) === 'system').length;
    const envNote = envCount
      ? ` ${envCount} ${envCount === 1 ? 'is an environment policy that applies' : 'are environment policies that apply'} to every project.`
      : '';
    if (!window.confirm(`${verb} ${targets.length} ${label} ${targets.length === 1 ? 'policy' : 'policies'}?${envNote}`)) {
      return;
    }
    setDisablingGroupKey(groupKey);
    const succeeded = new Set();
    const failed = [];
    for (const policy of targets) {
      const policyId = policy.name?.split('/').pop();
      if (!policyId) {
        failed.push(policy.displayName || 'unknown');
        continue;
      }
      try {
        if (policy?.userLabels?.owner === 'project') {
          await api.updateProjectPolicyEnabled(policyId, enabled);
        } else {
          await api.updatePolicyEnabled(policyId, enabled);
        }
        succeeded.add(policy.name);
      } catch (e) {
        failed.push(policy.displayName || policyId);
      }
    }
    if (succeeded.size > 0) {
      setPoliciesData((prev) => {
        if (!prev || !Array.isArray(prev.policies)) return prev;
        return {
          ...prev,
          policies: prev.policies.map((p) => (succeeded.has(p.name) ? { ...p, enabled } : p)),
        };
      });
    }
    if (failed.length > 0) {
      setPoliciesError(`Failed to ${enabled ? 'enable' : 'disable'}: ${failed.join(', ')}`);
    }
    setDisablingGroupKey(null);
  }, [disablingGroupKey, getPolicyOwner, isEnvUser, policiesReadOnly]);

  const handleDeleteGroupPolicies = useCallback(async (groupKey, policiesInGroup, projectId) => {
    const targets = [];
    const seen = new Set();
    (policiesInGroup || []).forEach((p) => {
      if (getPolicyOwner(p) === 'system' || seen.has(p.name)) return;
      seen.add(p.name);
      targets.push(p);
    });
    if (targets.length === 0 || deletingGroupKey) return;
    const component = targets[0]?.userLabels?.component;
    const label = COMPONENT_DISPLAY_NAMES[component] || component || 'this category';
    const noun = targets.length === 1 ? 'policy' : 'policies';
    if (!window.confirm(`Delete ${targets.length} ${label} ${noun} for ${projectId}? Environment policies are not deleted. This cannot be undone.`)) {
      return;
    }
    setDeletingGroupKey(groupKey);
    const succeeded = new Set();
    const failed = [];
    for (const policy of targets) {
      const policyId = policy.name.split('/').pop();
      if (!policyId) {
        failed.push(policy.displayName || policy.name);
        continue;
      }
      try {
        await api.deleteProjectPolicy(policyId);
        pendingCreatedPoliciesRef.current.delete(policy.name);
        succeeded.add(policy.name);
      } catch (err) {
        failed.push(policy.displayName || policyId);
      }
    }
    if (succeeded.size > 0) {
      setPoliciesData((prev) => {
        if (!prev || !Array.isArray(prev.policies)) return prev;
        return {
          ...prev,
          policies: prev.policies.filter((p) => !succeeded.has(p.name)),
        };
      });
    }
    if (failed.length > 0) {
      setPoliciesError(`Failed to delete: ${failed.join(', ')}`);
    }
    setDeletingGroupKey(null);
  }, [deletingGroupKey, getPolicyOwner]);

  const filteredPolicies = policies.filter((p) => {
    // Component filter
    if (componentFilter !== 'all') {
      const policyComponent = p.userLabels?.component || '';
      if (policyComponent !== componentFilter) return false;
    }
    // Search filter
    if (!policySearch) return true;
    const haystack = [
      p.displayName,
      ...(p.conditions || []).map((c) => c.displayName),
      ...(p.subscribers || []).map((s) => s.email),
      p.userLabels?.component,
      p.userLabels?.severity,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(policySearch.toLowerCase());
  });

  const { envPolicies, projectPolicies } = useMemo(() => {
    const envList = [];
    const projectList = [];
    filteredPolicies.forEach((p) => {
      const owner = getPolicyOwner(p);
      const policyEnv = getPolicyEnv(p);
      if (owner === 'system' && policyEnv === viewEnv) {
        envList.push(p);
      } else if (owner === 'project' && policyEnv === viewEnv) {
        projectList.push(p);
      }
    });

    const scopedProjectsSet = new Set(scopedAllowedProjects);
    const finalProjectList = isEnvUser
      ? projectList
      : projectList.filter((p) => {
          const projectIdLabel = p.userLabels?.project_id;
          return projectIdLabel && scopedProjectsSet.has(projectIdLabel);
        });

    return { envPolicies: envList, projectPolicies: finalProjectList };
  }, [filteredPolicies, getPolicyOwner, getPolicyEnv, viewEnv, scopedAllowedProjects, isEnvUser]);

  const groupPoliciesByComponent = useCallback((list) => {
    const groups = {};
    list.forEach((p) => {
      const component = p.userLabels?.component || 'other';
      if (!groups[component]) groups[component] = [];
      groups[component].push(p);
    });
    return Object.keys(groups).sort().reduce((acc, key) => {
      acc[key] = groups[key];
      return acc;
    }, {});
  }, []);

  const groupedEnvPolicies = useMemo(() => groupPoliciesByComponent(envPolicies), [envPolicies, groupPoliciesByComponent]);
  const visibleEnvGroups = useMemo(
    () => (showDisabled ? groupedEnvPolicies : withoutDisabled(groupedEnvPolicies)),
    [groupedEnvPolicies, showDisabled]
  );
  const envDisabledCount = useMemo(
    () => envPolicies.filter((p) => !p.enabled).length,
    [envPolicies]
  );
  const groupedProjectPoliciesByProject = useMemo(() => {
    const byProject = {};
    projectPolicies.forEach((p) => {
      const pid = p.userLabels?.project_id || 'unknown-project';
      if (!byProject[pid]) byProject[pid] = [];
      byProject[pid].push(p);
    });
    const sorted = Object.keys(byProject).sort().reduce((acc, key) => {
      acc[key] = groupPoliciesByComponent(byProject[key]);
      return acc;
    }, {});
    return sorted;
  }, [projectPolicies, groupPoliciesByComponent]);

  const catalogFetchedForRef = useRef('');
  useEffect(() => {
    if (tab !== 1 || !isEnvUser) return undefined;
    if (catalogFetchedForRef.current === env) return undefined;
    catalogFetchedForRef.current = env;
    let cancelled = false;
    (async () => {
      try {
        const result = await api.getEnvProjects();
        const ids = (Array.isArray(result?.projects) ? result.projects : [])
          .map((p) => String(p || '').trim())
          .filter((p) => p && !ENV_MARKER_IDS.has(p));
        if (ids.length > 0) {
          if (!cancelled) setEnvironmentProjectIds(Array.from(new Set(ids)).sort((a, b) => a.localeCompare(b)));
          return;
        }
      } catch {
        // Older API without /environments/projects: fall back to the status feeds.
      }
      const [networkResult, lmsResult] = await Promise.allSettled([
        api.getFactoryNetworkStatus(),
        api.getFactoryLmsStatus(),
      ]);
      if (cancelled) return;
      setEnvironmentProjectIds(collectEnvironmentProjectIds(
        networkResult.status === 'fulfilled' ? networkResult.value : null,
        lmsResult.status === 'fulfilled' ? lmsResult.value : null
      ));
    })();
    return () => { cancelled = true; };
  }, [tab, env, isEnvUser]);

  const creatableProjects = useMemo(() => {
    if (!isEnvUser || environmentProjectIds.length === 0) return scopedAllowedProjects;
    return Array.from(new Set([...scopedAllowedProjects, ...environmentProjectIds]))
      .sort((a, b) => a.localeCompare(b));
  }, [isEnvUser, environmentProjectIds, scopedAllowedProjects]);

  // Simple view: every project covered by an environment policy, plus any project
  // that has a policy of its own.
  const simpleProjectGroups = useMemo(() => {
    const ids = new Set();
    if (envPolicies.length > 0) {
      const catalog = environmentProjectIds.length > 0 ? environmentProjectIds : scopedAllowedProjects;
      catalog.forEach((pid) => ids.add(pid));
    }
    projectPolicies.forEach((p) => {
      const pid = p.userLabels?.project_id;
      if (!pid) return;
      const existing = Array.from(ids).find((id) => id.toLowerCase() === String(pid).toLowerCase());
      ids.add(existing || pid);
    });
    const byProject = {};
    Array.from(ids).sort((a, b) => a.localeCompare(b)).forEach((pid) => {
      const own = projectPolicies.filter(
        (p) => String(p.userLabels?.project_id || '').toLowerCase() === pid.toLowerCase()
      );
      const grouped = groupPoliciesByComponent([...own, ...envPolicies]);
      if (Object.keys(showDisabled ? grouped : withoutDisabled(grouped)).length === 0) return;
      byProject[pid] = grouped;
    });
    return byProject;
  }, [scopedAllowedProjects, environmentProjectIds, projectPolicies, envPolicies, groupPoliciesByComponent, showDisabled]);

  const visibleProjectGroups = policyView === 'simple' ? simpleProjectGroups : groupedProjectPoliciesByProject;
  
  const severityColors = SEVERITY_COLORS;
  const envSectionOpen = !!expandedGroups[ENV_SECTION_KEY] || !!policySearch;
  const projectSectionOpen = expandedGroups[PROJECT_SECTION_KEY] !== false || !!policySearch;
  const policyRowsVisible = useMemo(() => {
    const groupsFor = (groups) => (showDisabled ? groups : withoutDisabled(groups));
    const projectGroups = policyView === 'simple' ? simpleProjectGroups : groupedProjectPoliciesByProject;
    const projectsOpen = policyView === 'simple' || projectSectionOpen;
    if (projectsOpen) {
      const searchOpens = policyView === 'simple' && !!policySearch;
      for (const pid of Object.keys(projectGroups)) {
        if (!searchOpens && !expandedGroups[`project:${pid}`]) continue;
        const groups = groupsFor(projectGroups[pid]);
        for (const component of Object.keys(groups)) {
          if ((searchOpens || expandedGroups[`project:${pid}:${component}`]) && groups[component].length > 0) {
            return true;
          }
        }
      }
    }
    if (policyView === 'advanced' && envSectionOpen) {
      for (const component of Object.keys(visibleEnvGroups)) {
        if (expandedGroups[`env:${component}`] && visibleEnvGroups[component].length > 0) return true;
      }
    }
    return false;
  }, [
    policyView,
    simpleProjectGroups,
    groupedProjectPoliciesByProject,
    projectSectionOpen,
    envSectionOpen,
    expandedGroups,
    policySearch,
    showDisabled,
    visibleEnvGroups,
  ]);
  const policyColSpan = policyRowsVisible ? 5 : 1;
  const toggleSection = useCallback((key, defaultOpen) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !(prev[key] ?? defaultOpen) }));
  }, []);
  
  // Toggle group expansion
  const toggleGroup = useCallback((key) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);
  
  // Expand all groups
  const expandAllGroups = useCallback(() => {
    const allExpanded = { [ENV_SECTION_KEY]: true };
    Object.keys(groupedEnvPolicies).forEach((c) => {
      allExpanded[`env:${c}`] = true;
    });
    const projectGroups = policyView === 'simple' ? simpleProjectGroups : groupedProjectPoliciesByProject;
    Object.keys(projectGroups).forEach((pid) => {
      allExpanded[`project:${pid}`] = true;
      Object.keys(projectGroups[pid]).forEach((c) => {
        allExpanded[`project:${pid}:${c}`] = true;
      });
    });
    setExpandedGroups(allExpanded);
  }, [groupedEnvPolicies, groupedProjectPoliciesByProject, simpleProjectGroups, policyView]);
  
  // Collapse all groups
  const collapseAllGroups = useCallback(() => {
    setExpandedGroups({});
  }, []);

  // Fetch policy templates
  const fetchPolicyTemplates = useCallback(async () => {
    if (Object.keys(policyTemplates).length > 0) return; // Already loaded
    setTemplatesLoading(true);
    try {
      const result = await api.getPolicyTemplates();
      setPolicyTemplates(result.templates || {});
    } catch (err) {
      console.error('Error fetching policy templates:', err);
    } finally {
      setTemplatesLoading(false);
    }
  }, [policyTemplates]);

  // Open create policy dialog
  const handleOpenCreatePolicy = useCallback(() => {
    setCreatePolicyForm({
      component: '',
      templateId: '',
      templateParams: {},
      severity: 'warning',
      displayName: '',
      threshold: '',
      duration: '',
      evaluationInterval: '',
      projectPattern: '',
      projectId: '',
      owner: isEnvUser ? 'system' : 'project',
      customQuery: '',
      useCustomQuery: false,
      autoClose: '7d',
      selectedChannels: [],
      newEmailChannel: '',
    });
    setCreatePolicyError(null);
    setCreateMode('simple');
    setCreatePolicyOpen(true);
    fetchPolicyTemplates();
    fetchNotificationChannels();
  }, [fetchPolicyTemplates, fetchNotificationChannels, isEnvUser]);

  const selectedCreateTemplate = useMemo(() => {
    if (!createPolicyForm.component || !createPolicyForm.templateId) return null;
    return (policyTemplates[createPolicyForm.component] || []).find(
      (t) => t.id === createPolicyForm.templateId
    ) || null;
  }, [createPolicyForm.component, createPolicyForm.templateId, policyTemplates]);

  // Handle form field change
  const handleCreateFormChange = useCallback((field, value) => {
    setCreatePolicyForm((prev) => {
      const updated = { ...prev, [field]: value };
      // Reset template when component changes
      if (field === 'component') {
        updated.templateId = '';
        updated.templateParams = {};
        updated.severity = 'warning';
        updated.threshold = '';
        updated.duration = '';
        updated.evaluationInterval = '';
      }
      // Populate defaults from template
      if (field === 'templateId' && value && policyTemplates[prev.component]) {
        const template = policyTemplates[prev.component].find((t) => t.id === value);
        if (template) {
          updated.threshold = template.defaultThreshold?.toString() || '';
          updated.duration = template.defaultDuration || '';
          updated.evaluationInterval = template.defaultEvaluationInterval || '';
          updated.severity = template.severity || 'warning';
          updated.displayName = ''; // Let backend generate
          const defaults = {};
          (template.params || []).forEach((p) => {
            if (!p?.key) return;
            defaults[p.key] = p.default ?? '';
          });
          updated.templateParams = defaults;
        }
      }
      return updated;
    });
  }, [policyTemplates]);

  const handleCreateTemplateParamChange = useCallback((key, value) => {
    setCreatePolicyForm((prev) => ({
      ...prev,
      templateParams: {
        ...(prev.templateParams || {}),
        [key]: value,
      },
    }));
  }, []);

  // Build guided-tour steps AFTER create handlers exist (avoids TDZ)
  const tourSteps = useMemo(() => {
    const common = [
      {
        title: 'Open Policies',
        body: 'Go to Policies to create a new alert policy from a template.',
        focusId: 'tour-tab-policies',
        onEnter: () => setTab(1),
      },
      {
        title: 'Create a new policy',
        body: 'Click Create Policy to open the policy creation form.',
        focusId: 'tour-create-policy-btn',
        onEnter: () => setTab(1),
      },
    ];

    const lmsFlow = [
      {
        title: 'Select component: LMS',
        body: 'Choose LMS. Then select a template (e.g., Lamps OFF/ON Percentage Time Window).',
        focusId: 'tour-create-component',
        onEnter: () => {
          setTab(1);
          handleOpenCreatePolicy();
          window.setTimeout(() => {
            setCreateMode('advanced');
            handleCreateFormChange('component', 'lms');
          }, 0);
        },
      },
      {
        title: 'Pick a template and set threshold',
        body:
          'Pick a template. Set threshold and (if offered) time window + comparison operator. Choose severity and channels.',
        focusId: 'tour-create-template',
      },
      {
        title: 'Create the policy',
        body: 'Click Create Policy to save it. The tool will start evaluating it immediately.',
        focusId: 'tour-create-submit',
      },
      {
        title: 'Go to Alerts',
        body: 'Switch to Alerts to see incidents when policies trigger. Use filters/search to find LMS alerts.',
        focusId: 'tour-tab-alerts',
        onEnter: () => {
          setCreatePolicyOpen(false);
          setTab(0);
        },
      },
      {
        title: 'Open an alert for details',
        body:
          'Click an alert row to open details. For LMS you’ll see lamps ON/OFF and reachability metrics to speed up triage.',
        focusId: 'tour-alerts-table',
      },
    ];

    const nmFlow = [
      {
        title: 'Select component: Network Manager',
        body: 'Choose Network Manager. Then select a template like Offline Percentage / Packet Loss / RSSI.',
        focusId: 'tour-create-component',
        onEnter: () => {
          setTab(1);
          handleOpenCreatePolicy();
          window.setTimeout(() => {
            setCreateMode('advanced');
            handleCreateFormChange('component', 'network_manager');
          }, 0);
        },
      },
      {
        title: 'Pick a template and set threshold',
        body:
          'Pick a Network Manager template, set threshold/duration, choose severity and notification channels.',
        focusId: 'tour-create-template',
      },
      {
        title: 'Create the policy',
        body: 'Click Create Policy to save it. Then switch to Alerts to see triggered incidents.',
        focusId: 'tour-create-submit',
      },
      {
        title: 'Go to Alerts',
        body: 'Switch to Alerts. Use filters/search to find Network Manager alerts quickly.',
        focusId: 'tour-tab-alerts',
        onEnter: () => {
          setCreatePolicyOpen(false);
          setTab(0);
        },
      },
      {
        title: 'Open an alert for device context',
        body:
          'Open an alert to see enriched context like offline device lists and aggregated network stats.',
        focusId: 'tour-alerts-table',
      },
    ];

    return tourMode === 'network_manager'
      ? [...common, ...nmFlow]
      : [...common, ...lmsFlow];
  }, [tourMode, handleCreateFormChange, handleOpenCreatePolicy]);

  useEffect(() => {
    if (!tourOpen) return;
    const step = tourSteps[tourStep];
    if (!step) return;
    if (typeof step.onEnter === 'function') step.onEnter();
    window.setTimeout(() => focusTourEl(step.focusId), 50);
  }, [tourOpen, tourStep, tourSteps, focusTourEl]);

  // Add new email channel
  const handleAddEmailChannel = useCallback(async () => {
    const email = createPolicyForm.newEmailChannel.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    
    setAddingEmailChannel(true);
    try {
      const result = await api.createEmailNotificationChannel(email);
      if (result.channel) {
        // Add to notification channels list
        setNotificationChannels((prev) => {
          // Check if already exists
          if (prev.some((ch) => ch.name === result.channel.name)) return prev;
          return [...prev, result.channel];
        });
        // Select the new channel
        setCreatePolicyForm((prev) => ({
          ...prev,
          selectedChannels: [...prev.selectedChannels, result.channel.name],
          newEmailChannel: '',
        }));
      }
    } catch (err) {
      console.error('Error creating email channel:', err);
      setCreatePolicyError(err.message || 'Failed to create email channel');
    } finally {
      setAddingEmailChannel(false);
    }
  }, [createPolicyForm.newEmailChannel]);

  const handleAddEmailChannelToEdit = useCallback(async () => {
    const email = editPolicyForm.newEmailChannel.trim().toLowerCase();
    if (!email || !email.includes('@')) return;

    setAddingEmailChannel(true);
    try {
      const result = await api.createEmailNotificationChannel(email);
      if (result.channel) {
        setNotificationChannels((prev) => {
          if (prev.some((ch) => ch.name === result.channel.name)) return prev;
          return [...prev, result.channel];
        });
        setEditPolicyForm((prev) => ({
          ...prev,
          selectedChannels: [...prev.selectedChannels, result.channel.name],
          newEmailChannel: '',
        }));
      }
    } catch (err) {
      console.error('Error creating email channel:', err);
    } finally {
      setAddingEmailChannel(false);
    }
  }, [editPolicyForm.newEmailChannel]);

  // Create policy
  const handleCreatePolicy = useCallback(async () => {
    setCreatingPolicy(true);
    setCreatePolicyError(null);
    try {
      if ((!isEnvUser || createPolicyForm.owner === 'project') && !createPolicyForm.projectId) {
        setCreatePolicyError('Please select a project for this policy.');
        return;
      }
      const autoCloseSeconds = parseDurationInput(createPolicyForm.autoClose);
      if (!autoCloseSeconds) {
        setCreatePolicyError('Auto close duration must be a number with units (e.g., 30m, 6h, 7d).');
        return;
      }
      const policyData = {
        component: createPolicyForm.component,
        templateId: createPolicyForm.useCustomQuery ? undefined : createPolicyForm.templateId,
        templateParams:
          createPolicyForm.useCustomQuery ? undefined : (createPolicyForm.templateParams || undefined),
        severity: createPolicyForm.severity || undefined,
        displayName: createPolicyForm.displayName || undefined,
        query: createPolicyForm.useCustomQuery ? createPolicyForm.customQuery : undefined,
        threshold: createPolicyForm.threshold ? parseFloat(createPolicyForm.threshold) : undefined,
        duration: createPolicyForm.duration || undefined,
        evaluationInterval: createPolicyForm.evaluationInterval || undefined,
        autoClose: autoCloseSeconds,
        env: env || undefined,
        owner: isEnvUser ? createPolicyForm.owner : 'project',
        projectPattern:
          isEnvUser && createPolicyForm.owner !== 'project'
            ? (createPolicyForm.projectPattern || undefined)
            : undefined,
        projectId:
          !isEnvUser || createPolicyForm.owner === 'project'
            ? (createPolicyForm.projectId || undefined)
            : undefined,
        enabled: true,
        notificationChannels: createPolicyForm.selectedChannels,
      };
      
      await api.createPolicy(policyData);
      setCreatePolicyOpen(false);
      // Refresh policies
      fetchPolicies();
    } catch (err) {
      console.error('Error creating policy:', err);
      setCreatePolicyError(err.message || 'Failed to create policy');
    } finally {
      setCreatingPolicy(false);
    }
  }, [createPolicyForm, fetchPolicies, isEnvUser, parseDurationInput, env]);

  // Column resize handler
  const handleColumnResize = useCallback((columnKey, startX, startWidth) => {
    const onMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const tableWidth = document.querySelector('.policies-table')?.offsetWidth || 1000;
      const deltaPercent = (deltaX / tableWidth) * 100;
      const newWidth = Math.max(5, Math.min(50, startWidth + deltaPercent));
      
      setColumnWidths((prev) => ({
        ...prev,
        [columnKey]: newWidth,
      }));
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          sx={{ flex: 1, '& .MuiTab-root': { minWidth: 0, px: 0, mr: 3 } }}
        >
          <Tab id="tour-tab-alerts" label="Alerts" />
          <Tab id="tour-tab-policies" label="Policies" />
        </Tabs>
        {tab === 0 && (
          <Tooltip title="Refresh Alerts">
            <IconButton
              onClick={refreshAlertsToNow}
              disabled={alertsLoading}
              size="small"
              sx={{ width: 32, height: 32, padding: 0, flexShrink: 0, color: 'text.secondary' }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {tab === 1 && (
          <Tooltip title="Refresh Policies">
            <IconButton
              onClick={fetchPolicies}
              disabled={policiesLoading}
              size="small"
              sx={{ width: 32, height: 32, padding: 0, flexShrink: 0, color: 'text.secondary' }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Help: Guided policy tour">
          <IconButton
            onClick={() => setTourOpen(true)}
            size="small"
            sx={{ width: 32, height: 32, padding: 0, flexShrink: 0, color: 'text.secondary' }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {tab === 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search alerts"
              value={alertSearch}
              onChange={(e) => setAlertSearch(e.target.value)}
              sx={{ width: { xs: '100%', sm: 260 } }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select value={alertFilter} onChange={(e) => setAlertFilter(e.target.value)}>
                <MenuItem value="all">All states</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <Select value={alertComponentFilter} onChange={(e) => setAlertComponentFilter(e.target.value)}>
                <MenuItem value="all">All components</MenuItem>
                {availableAlertComponents.map((c) => (
                  <MenuItem key={c} value={c}>
                    {COMPONENT_DISPLAY_NAMES[c] || c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            {[
              { label: 'From', value: startTime, onChange: setStartTime },
              { label: 'To', value: endTime, onChange: setEndTime },
            ].map(({ label, value, onChange }) => (
              <TextField
                key={label}
                size="small"
                label={label}
                type="datetime-local"
                inputProps={{ step: 1 }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  width: 220,
                  '& input': { colorScheme: theme.palette.mode, fontVariantNumeric: 'tabular-nums' },
                  '& input::-webkit-calendar-picker-indicator': { opacity: 0.5, cursor: 'pointer' },
                }}
                value={toLocalDatetimeLocalValue(value)}
                onChange={(e) => {
                  const dt = fromLocalDatetimeLocalValue(e.target.value);
                  if (dt) onChange(dt);
                }}
              />
            ))}
          </Box>

          {(selectedAlertIds.size > 0 || alertComponentFilter !== 'all') && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
              {selectedAlertIds.size > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={deletingAlerts}
                  onClick={handleDeleteSelectedAlerts}
                  sx={{ color: 'error.main' }}
                >
                  {deletingAlerts ? <CircularProgress size={16} /> : `Delete selected (${selectedAlertIds.size})`}
                </Button>
              )}
              {alertComponentFilter !== 'all' && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={deletingAlerts}
                  onClick={handleDeleteComponentAlerts}
                  sx={{ color: 'error.main' }}
                >
                  {deletingAlerts ? <CircularProgress size={16} /> : `Delete all ${COMPONENT_DISPLAY_NAMES[alertComponentFilter] || alertComponentFilter} alerts`}
                </Button>
              )}
            </Box>
          )}

          {alertsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : alertsError ? (
            <Alert severity="error">{alertsError}</Alert>
          ) : (
            <Paper sx={{ borderRadius: '10px', border: 1, borderColor: 'divider', overflow: 'hidden', boxShadow: CARD_SHADOW }}>
              <TableContainer id="tour-alerts-table">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allFilteredSelected}
                          indeterminate={selectedAlertIds.size > 0 && !allFilteredSelected}
                          onChange={(e) => handleSelectAllFilteredAlerts(e.target.checked)}
                          inputProps={{ 'aria-label': 'select all alerts' }}
                        />
                      </TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Project</TableCell>
                      <TableCell>Component</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Policy</TableCell>
                      <TableCell>State</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAlerts.map((a) => {
                      const isOpen = a.state === 'open';
                      const alertSeverity = a.severity || 'warning';
                      const alertSeverityStyle = severityColors[alertSeverity] || {};
                      const alertId = a.id || '';
                      const isSelected = alertId ? selectedAlertIds.has(alertId) : false;
                      return (
                        <TableRow
                          key={a.incidentId || a.id}
                          hover
                          sx={{ 
                            cursor: 'pointer',
                            bgcolor: 'transparent',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                          onClick={() => {
                            setSelectedAlert(a);
                            setDetailsOpen(true);
                          }}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isSelected}
                              disabled={!alertId}
                              onChange={(e) => toggleAlertSelection(alertId, e.target.checked)}
                              inputProps={{ 'aria-label': `select alert ${a.incidentId || a.id}` }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.8rem' }}>
                            {formatTs(a.startedAt)}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.8rem' }}>
                            {formatDuration(a.startedAt, a.endedAt)}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{a.projectId || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={COMPONENT_DISPLAY_NAMES[a.component] || a.component || '-'}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={alertSeverity}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                bgcolor: alertSeverityStyle.bg,
                                color: alertSeverityStyle.color,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{a.policyName || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={a.state || '-'}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                bgcolor: isOpen ? statusTint(STATUS.critical) : NEUTRAL[100],
                                color: textFor(isOpen ? STATUS.critical : 'text.secondary'),
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredAlerts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No alerts in the selected range.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <AlertDetailsDialog
            open={detailsOpen}
            onClose={() => setDetailsOpen(false)}
            event={selectedAlert}
            demoDataId="monitoring-alert-details"
          />
        </>
      )}

      {tab === 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Component</InputLabel>
              <Select
                value={componentFilter}
                label="Component"
                onChange={(e) => setComponentFilter(e.target.value)}
              >
                <MenuItem value="all">All Components</MenuItem>
                {availableComponents.map((c) => (
                  <MenuItem key={c} value={c}>
                    {COMPONENT_DISPLAY_NAMES[c] || c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Search Policies"
              value={policySearch}
              onChange={(e) => setPolicySearch(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <ToggleButtonGroup
              value={showDisabled ? 'all' : 'enabled'}
              exclusive
              size="small"
              aria-label="Which policies to show"
              onChange={(_e, value) => {
                if (value) setShowDisabled(value === 'all');
              }}
            >
              <ToggleButton value="enabled" sx={{ p: 0 }}>
                <Tooltip title="Only policies that are turned on">
                  <Box component="span" sx={{ px: '9px', py: '6px' }}>Enabled</Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="all" sx={{ p: 0 }}>
                <Tooltip title="Enabled and disabled policies">
                  <Box component="span" sx={{ px: '9px', py: '6px' }}>All</Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              value={policyView}
              exclusive
              size="small"
              aria-label="Policy view"
              onChange={(_e, value) => value && setPolicyView(value)}
            >
              <ToggleButton value="simple" sx={{ p: 0 }}>
                <Tooltip title="Each project, including the environment policies that cover it">
                  <Box component="span" sx={{ px: '9px', py: '6px' }}>Simple</Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="advanced" sx={{ p: 0 }}>
                <Tooltip title="Project policies and environment policies in separate sections">
                  <Box component="span" sx={{ px: '9px', py: '6px' }}>Advanced</Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={expandAllGroups}>
                Expand All
              </Button>
              <Button size="small" variant="outlined" onClick={collapseAllGroups}>
                Collapse All
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreatePolicy}
                disabled={!canCreatePolicy}
                id="tour-create-policy-btn"
              >
                Create Policy
              </Button>
            </Box>
          </Box>
          {policiesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : policiesError ? (
            <Alert severity="error">{policiesError}</Alert>
          ) : (
            <Paper sx={{ borderRadius: '10px', border: 1, borderColor: 'divider', overflow: 'hidden', boxShadow: CARD_SHADOW }}>
              <TableContainer>
                <Table size="small" className="policies-table" sx={{ tableLayout: policyRowsVisible ? 'fixed' : 'auto', width: '100%' }}>
                  {policyRowsVisible && (
                    <colgroup>
                      <col style={{ width: `${columnWidths.displayName}%` }} />
                      <col style={{ width: `${columnWidths.enabled}%` }} />
                      <col style={{ width: `${columnWidths.conditions}%` }} />
                      <col style={{ width: `${columnWidths.subscription}%` }} />
                      <col style={{ width: `${columnWidths.actions}%` }} />
                    </colgroup>
                  )}
                  {policyRowsVisible && (
                    <TableHead>
                      <PolicyColumnsHeader columnWidths={columnWidths} onResize={handleColumnResize} />
                    </TableHead>
                  )}
                  <TableBody>
                    {policyView === 'advanced' && (
                    <TableRow sx={SECTION_ROW_SX} onClick={() => toggleSection(PROJECT_SECTION_KEY, true)}>
                      <TableCell colSpan={policyColSpan} sx={SECTION_CELL_SX}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {projectSectionOpen ? (
                            <ExpandMoreIcon sx={{ fontSize: '1.2rem' }} />
                          ) : (
                            <ChevronRightIcon sx={{ fontSize: '1.2rem' }} />
                          )}
                          Project Policies
                          <Typography component="span" variant="caption" color="text.secondary" sx={SECTION_META_SX}>
                            {Object.keys(groupedProjectPoliciesByProject).length} projects · {projectPolicies.filter((p) => p.enabled).length} active of {projectPolicies.length}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                    )}
                    {(policyView === 'simple' || projectSectionOpen) && (
                    <>
                    {Object.keys(visibleProjectGroups).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={policyColSpan} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                          {policyView === 'simple' ? 'No projects found.' : 'No project policies found.'}
                        </TableCell>
                      </TableRow>
                    )}
                    {Object.keys(visibleProjectGroups).map((pid) => {
                      const projectKey = `project:${pid}`;
                      const projectExpanded = !!expandedGroups[projectKey] || (policyView === 'simple' && !!policySearch);
                      const allGroups = visibleProjectGroups[pid];
                      const disabledCount = countDisabled(allGroups);
                      const componentGroups = showDisabled ? allGroups : withoutDisabled(allGroups);
                      return (
                      <React.Fragment key={`project-${pid}`}>
                        <TableRow sx={GROUP_ROW_SX} onClick={() => toggleGroup(projectKey)}>
                          <TableCell colSpan={policyColSpan} sx={{ ...GROUP_CELL_SX, pl: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {projectExpanded ? (
                                <ExpandMoreIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                              ) : (
                                <ChevronRightIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                              )}
                              {pid}
                              <Chip
                                label={Object.values(componentGroups).reduce((sum, list) => sum + list.length, 0)}
                                size="small"
                                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                              />
                              <Box sx={{ display: 'flex', gap: 0.75, ml: 'auto', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                {summarizePolicyCategories(componentGroups).map(({ key, label, Icon, total, active }) => (
                                  <Tooltip
                                    key={key}
                                    title={`${label}: ${active} active of ${total} ${total === 1 ? 'policy' : 'policies'}`}
                                  >
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        px: 1,
                                        height: 22,
                                        borderRadius: 999,
                                        bgcolor: 'background.paper',
                                        border: `1px solid ${NEUTRAL[200]}`,
                                        color: active > 0 ? 'text.primary' : 'text.disabled',
                                        fontSize: '0.72rem',
                                        fontWeight: 500,
                                      }}
                                    >
                                      <Icon sx={{ fontSize: 14 }} />
                                      {label}
                                      <Box component="span" sx={{ color: 'text.secondary' }}>{total}</Box>
                                    </Box>
                                  </Tooltip>
                                ))}
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                        {projectExpanded && Object.keys(componentGroups).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={policyColSpan} sx={{ pl: 7, py: 1.5, color: 'text.secondary' }}>
                              {disabledCount > 0
                                ? 'No enabled policies for this project.'
                                : 'No policies apply to this project.'}
                            </TableCell>
                          </TableRow>
                        )}
                        {projectExpanded &&
                          Object.keys(componentGroups).map((component) => {
                            const componentKey = `project:${pid}:${component}`;
                            const componentExpanded = !!expandedGroups[componentKey] || (policyView === 'simple' && !!policySearch);
                            const list = componentGroups[component];
                            return (
                              <React.Fragment key={`project-${pid}-${component}`}>
                                <TableRow sx={GROUP_ROW_SX} onClick={() => toggleGroup(componentKey)}>
                                  <TableCell colSpan={policyColSpan} sx={{ ...GROUP_CELL_SX, fontWeight: 500, pl: 7 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      {componentExpanded ? (
                                        <ExpandMoreIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                                      ) : (
                                        <ChevronRightIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                                      )}
                                      {COMPONENT_DISPLAY_NAMES[component] || component}
                                      <Chip label={list.length} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                                      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {policyView === 'simple' && (() => {
                                          const projectOwned = list.filter((p) => getPolicyOwner(p) !== 'system');
                                          const deleteButton = (
                                            <Button
                                              size="small"
                                              disabled={
                                                deletingGroupKey === componentKey
                                                || disablingGroupKey === componentKey
                                                || projectOwned.length === 0
                                              }
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteGroupPolicies(componentKey, list, pid);
                                              }}
                                              sx={{ minWidth: 0, color: 'text.secondary' }}
                                            >
                                              {deletingGroupKey === componentKey ? <CircularProgress size={14} /> : 'Delete'}
                                            </Button>
                                          );
                                          return projectOwned.length === 0 ? (
                                            <Tooltip title="Environment policies apply to every project. Delete them from Advanced view.">
                                              <span>{deleteButton}</span>
                                            </Tooltip>
                                          ) : deleteButton;
                                        })()}
                                        <Button
                                          size="small"
                                          disabled={
                                            disablingGroupKey === componentKey
                                            || deletingGroupKey === componentKey
                                            || !list.some((p) => getPolicyOwner(p) !== 'system' || (isEnvUser && !policiesReadOnly))
                                          }
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSetGroupEnabled(componentKey, list, !list.some((p) => p.enabled));
                                          }}
                                          sx={{ minWidth: 0, color: 'text.secondary' }}
                                        >
                                          {disablingGroupKey === componentKey
                                            ? <CircularProgress size={14} />
                                            : (list.some((p) => p.enabled) ? 'Disable' : 'Enable')}
                                        </Button>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                                {componentExpanded &&
                                  list.map((p) => {
                                    const policyId = p.name.split('/').pop();
                                    const isUpdating = updatingPolicyId === policyId;
                                    const isDeleting = deletingPolicyId === policyId;
                                    const envPolicyInSimple = policyView === 'simple' && getPolicyOwner(p) === 'system';
                                    const severity = p.userLabels?.severity;
                                    const severityStyle = severityColors[severity] || {};
                                    return (
                                      <TableRow key={p.name}>
                                        <TableCell
                                          sx={{
                                            pl: 10,
                                            fontFamily: 'monospace',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {getPolicyOwner(p) === 'system' && (
                                              <Chip
                                                label="Environment"
                                                size="small"
                                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                                              />
                                            )}
                                            {severity && (
                                              <Chip
                                                label={severity}
                                                size="small"
                                                sx={{
                                                  height: 18,
                                                  fontSize: '0.65rem',
                                                  fontWeight: 600,
                                                  textTransform: 'uppercase',
                                                  bgcolor: severityStyle.bg,
                                                  color: severityStyle.color,
                                                }}
                                              />
                                            )}
                                            <span>{p.displayName}</span>
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Switch
                                            checked={p.enabled}
                                            onChange={(e) => handleTogglePolicy(p, e.target.checked)}
                                            disabled={
                                              isUpdating
                                              || isDeleting
                                              || (getPolicyOwner(p) === 'system' && (!isEnvUser || policiesReadOnly))
                                            }
                                            size="small"
                                          />
                                          {isUpdating && (
                                            <CircularProgress size={16} sx={{ ml: 1, verticalAlign: 'middle' }} />
                                          )}
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>
                                          {(p.conditions || []).map((c, idx) => (
                                            <Box key={c.name || idx} sx={{ mb: 0.5 }}>
                                              <Box sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{c.displayName || '-'}</Box>
                                              {c.query && (
                                                <Box
                                                  sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.75rem',
                                                    color: 'text.secondary',
                                                    mt: 0.25,
                                                    wordBreak: 'break-word',
                                                  }}
                                                >
                                                  {c.query}
                                                </Box>
                                              )}
                                              {c.query && (
                                                <Tooltip title="Edit policy">
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenEditPolicy(p)}
                                                    disabled={isUpdating || isDeleting}
                                                    sx={{ mt: 0.25, p: 0.25 }}
                                                  >
                                                    <EditIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                              )}
                                            </Box>
                                          ))}
                                          {(!p.conditions || p.conditions.length === 0) && '-'}
                                        </TableCell>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(p.subscribers || []).map((s) => {
                                              const key = s.email ? `${policyId}:${s.email}` : `${policyId}:${s.channelName}`;
                                              const label = s.email || s.displayName || s.channelName;
                                              const canDelete = !!s.email;
                                              return (
                                                <Chip
                                                  key={key}
                                                  label={label}
                                                  size="small"
                                                  onDelete={canDelete ? () => handleUnsubscribe(p, s.email) : undefined}
                                                  deleteIcon={
                                                    canDelete && unsubscribingKey === key ? (
                                                      <CircularProgress size={12} />
                                                    ) : undefined
                                                  }
                                                  disabled={isUpdating || isDeleting || (canDelete && unsubscribingKey === key)}
                                                  sx={{
                                                    bgcolor: s.email ? undefined : 'action.hover',
                                                    color: s.email ? undefined : 'text.secondary',
                                                  }}
                                                />
                                              );
                                            })}
                                            <Tooltip title="Subscribe by email">
                                              <IconButton
                                                size="small"
                                                onClick={() => handleSubscribeClick(p)}
                                                disabled={isUpdating || isDeleting}
                                                color="primary"
                                                sx={{
                                                  ml: 0.5,
                                                  width: 28,
                                                  height: 28,
                                                  padding: 0.5,
                                                }}
                                              >
                                                <EmailIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Tooltip
                                            title={envPolicyInSimple
                                              ? 'This policy belongs to the environment and applies to every project. Delete it from Advanced view.'
                                              : 'Delete policy'}
                                          >
                                            <span>
                                              <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (envPolicyInSimple) return;
                                                  handleDeletePolicy(p);
                                                }}
                                                disabled={envPolicyInSimple || isUpdating || isDeleting}
                                                color="error"
                                              >
                                                {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                              </React.Fragment>
                            );
                          })}
                      </React.Fragment>
                      );
                    })}
                    </>
                    )}
                    {policyView === 'advanced' && (
                    <>
                    <TableRow sx={SECTION_ROW_SX} onClick={() => toggleSection(ENV_SECTION_KEY, false)}>
                      <TableCell colSpan={policyColSpan} sx={SECTION_CELL_SX}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {envSectionOpen ? (
                            <ExpandMoreIcon sx={{ fontSize: '1.2rem' }} />
                          ) : (
                            <ChevronRightIcon sx={{ fontSize: '1.2rem' }} />
                          )}
                          Environment Policies
                          <Typography component="span" variant="caption" color="text.secondary" sx={SECTION_META_SX}>
                            {envPolicies.filter((p) => p.enabled).length} active of {envPolicies.length} · applies to all projects in the environment
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {envSectionOpen && (
                    <>
                    {Object.keys(visibleEnvGroups).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={policyColSpan} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                          {envDisabledCount > 0 && !showDisabled
                            ? 'No enabled environment policies.'
                            : 'No environment policies found.'}
                        </TableCell>
                      </TableRow>
                    )}
                    {Object.keys(visibleEnvGroups).map((component) => {
                      const groupKey = `env:${component}`;
                      const isExpanded = !!expandedGroups[groupKey];
                      const isEnvReadOnly = !isEnvUser || policiesReadOnly;
                      return (
                      <React.Fragment key={`env-${component}`}>
                        <TableRow sx={GROUP_ROW_SX} onClick={() => toggleGroup(groupKey)}>
                          <TableCell colSpan={policyColSpan} sx={{ ...GROUP_CELL_SX, pl: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {isExpanded ? (
                                <ExpandMoreIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                              ) : (
                                <ChevronRightIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                              )}
                              {COMPONENT_DISPLAY_NAMES[component] || component}
                              <Chip
                                label={visibleEnvGroups[component].length}
                                size="small"
                                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                              />
                              <Box sx={{ ml: 'auto' }}>
                                <Button
                                  size="small"
                                  disabled={isEnvReadOnly || disablingGroupKey === groupKey}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const groupPolicies = visibleEnvGroups[component];
                                    handleSetGroupEnabled(groupKey, groupPolicies, !groupPolicies.some((p) => p.enabled));
                                  }}
                                  sx={{ minWidth: 0, color: 'text.secondary' }}
                                >
                                  {disablingGroupKey === groupKey
                                    ? <CircularProgress size={14} />
                                    : (visibleEnvGroups[component].some((p) => p.enabled) ? 'Disable' : 'Enable')}
                                </Button>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                        {isExpanded && visibleEnvGroups[component].map((p) => {
                          const policyId = p.name.split('/').pop();
                          const isUpdating = updatingPolicyId === policyId;
                          const isDeleting = deletingPolicyId === policyId;
                          const severity = p.userLabels?.severity;
                          const severityStyle = severityColors[severity] || {};
                          return (
                            <TableRow key={p.name} sx={isEnvReadOnly ? { opacity: 0.6 } : undefined}>
                              <TableCell
                                sx={{
                                  pl: 7,
                                  fontFamily: 'monospace',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {severity && (
                                    <Chip
                                      label={severity}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        bgcolor: severityStyle.bg,
                                        color: severityStyle.color,
                                      }}
                                    />
                                  )}
                                  <span>{p.displayName}</span>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Switch
                                  checked={p.enabled}
                                  onChange={(e) => handleTogglePolicy(p, e.target.checked)}
                                  disabled={isEnvReadOnly || isUpdating || isDeleting}
                                  size="small"
                                />
                                {isUpdating && (
                                  <CircularProgress size={16} sx={{ ml: 1, verticalAlign: 'middle' }} />
                                )}
                              </TableCell>
                              <TableCell sx={{ color: 'text.secondary' }}>
                                {(p.conditions || []).map((c, idx) => (
                                  <Box key={c.name || idx} sx={{ mb: 0.5 }}>
                                    <Box sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{c.displayName || '-'}</Box>
                                    {(c.query || c.filter) && (
                                      <Box
                                        sx={{
                                          fontFamily: 'monospace',
                                          fontSize: '0.75rem',
                                          color: 'text.secondary',
                                          mt: 0.25,
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {c.query || c.filter}
                                      </Box>
                                    )}
                                    {(c.query || c.filter) && (
                                      <Tooltip
                                        title={
                                          isEnvReadOnly
                                            ? 'Read-only: missing env marker in allowed projects'
                                            : 'Edit policy'
                                        }
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() => handleOpenEditPolicy(p, c)}
                                          disabled={isEnvReadOnly || isUpdating || isDeleting}
                                          sx={{ mt: 0.25, p: 0.25 }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                ))}
                                {(!p.conditions || p.conditions.length === 0) && '-'}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {(p.subscribers || []).map((s) => {
                                    const key = s.email ? `${policyId}:${s.email}` : `${policyId}:${s.channelName}`;
                                    const label = s.email || s.displayName || s.channelName;
                                    const canDelete = !!s.email && !isEnvReadOnly;
                                    return (
                                      <Chip
                                        key={key}
                                        label={label}
                                        size="small"
                                        onDelete={canDelete ? () => handleUnsubscribe(p, s.email) : undefined}
                                        deleteIcon={
                                          canDelete && unsubscribingKey === key ? (
                                            <CircularProgress size={12} />
                                          ) : undefined
                                        }
                                        disabled={isEnvReadOnly || isUpdating || isDeleting || (canDelete && unsubscribingKey === key)}
                                        sx={{
                                          bgcolor: s.email ? undefined : 'action.hover',
                                          color: s.email ? undefined : 'text.secondary',
                                        }}
                                      />
                                    );
                                  })}
                                  <Tooltip title={isEnvReadOnly ? 'Read-only: missing env marker in allowed projects' : 'Subscribe by email'}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleSubscribeClick(p)}
                                      disabled={isEnvReadOnly || isUpdating || isDeleting}
                                      color="primary"
                                      sx={{ 
                                        ml: 0.5,
                                        width: 28,
                                        height: 28,
                                        padding: 0.5,
                                      }}
                                    >
                                      <EmailIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title={isEnvReadOnly ? 'Read-only: missing env marker in allowed projects' : 'Delete policy'}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeletePolicy(p);
                                    }}
                                    disabled={isEnvReadOnly || isUpdating || isDeleting}
                                    color="error"
                                  >
                                    {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </React.Fragment>
                      );
                    })}
                    </>
                    )}
                    </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <Dialog
            open={editPolicyOpen}
            onClose={() => {
              setEditPolicyOpen(false);
              setEditingPolicy(null);
            }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Edit Policy</DialogTitle>
            
            <DialogContent>
              {editPolicyError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {editPolicyError}
                </Alert>
              )}
              {editingPolicy && (
                <Box sx={{ mt: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Display Name"
                        value={editPolicyForm.displayName}
                        onChange={(e) => handleEditPolicyFormChange('displayName', e.target.value)}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Component" value={editPolicyForm.component} fullWidth disabled />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Owner" value={editPolicyForm.owner} fullWidth disabled />
                    </Grid>
                    {editPolicyForm.owner === 'project' && (
                      <Grid item xs={12} sm={6}>
                        <TextField label="Project ID" value={editPolicyForm.projectId} fullWidth disabled />
                      </Grid>
                    )}
                  </Grid>

                  <TextField
                    label={editPolicyForm.conditionType === 'threshold' ? 'Condition Filter' : 'PromQL Query'}
                    fullWidth
                    multiline
                    rows={6}
                    value={editPolicyForm.customQuery}
                    onChange={(e) => handleEditPolicyFormChange('customQuery', e.target.value)}
                    disabled={editPolicyForm.conditionType !== 'threshold'}
                    sx={{ mt: 2, fontFamily: 'monospace', fontSize: '0.875rem' }}
                    helperText={
                      editPolicyForm.conditionType === 'threshold'
                        ? 'Filter-based condition (Cloud Monitoring threshold filter)'
                        : 'PromQL query is read-only (use threshold, duration, and evaluation interval fields to modify the policy)'
                    }
                  />

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Threshold"
                        value={editPolicyForm.threshold}
                        onChange={(e) => handleEditPolicyFormChange('threshold', e.target.value)}
                        fullWidth
                        inputMode="numeric"
                        placeholder="e.g., 95"
                        helperText="Alert triggers when metric exceeds this value"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Duration (e.g., 5m, 1h)"
                        value={editPolicyForm.duration}
                        onChange={(e) => handleEditPolicyFormChange('duration', e.target.value)}
                        fullWidth
                        placeholder="e.g., 300s, 5m"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Evaluation Interval (e.g., 60s)"
                        value={editPolicyForm.evaluationInterval}
                        onChange={(e) => handleEditPolicyFormChange('evaluationInterval', e.target.value)}
                        fullWidth
                        placeholder="e.g., 60s"
                        disabled={editPolicyForm.conditionType === 'threshold'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Auto-Close (e.g., 7d)"
                        value={editPolicyForm.autoClose}
                        onChange={(e) => handleEditPolicyFormChange('autoClose', e.target.value)}
                        fullWidth
                        placeholder="e.g., 3h, 7d"
                        disabled
                        helperText="Cannot be changed after policy creation (read-only)"
                      />
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Notification Channels
                  </Typography>
                  {channelsLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading channels...
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel>Select Notification Channels</InputLabel>
                        <Select
                          multiple
                          open={editChannelsMenuOpen}
                          onOpen={() => setEditChannelsMenuOpen(true)}
                          onClose={() => setEditChannelsMenuOpen(false)}
                          value={editPolicyForm.selectedChannels}
                          label="Select Notification Channels"
                          onChange={(e) => handleEditPolicyFormChange('selectedChannels', e.target.value)}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((channelName) => {
                                const channel = notificationChannels.find((c) => c.name === channelName);
                                return (
                                  <Chip
                                    key={channelName}
                                    label={channel?.displayName || channel?.email || channelName.split('/').pop()}
                                    size="small"
                                  />
                                );
                              })}
                            </Box>
                          )}
                        >
                          <ListSubheader
                            sx={{
                              position: 'sticky',
                              top: 0,
                              zIndex: 1,
                              bgcolor: 'background.paper',
                              display: 'flex',
                              justifyContent: 'flex-end',
                              py: 1,
                            }}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setEditChannelsMenuOpen(false);
                              }}
                            >
                              Done
                            </Button>
                          </ListSubheader>
                          {emailNotificationChannels.map((ch) => (
                            <MenuItem key={ch.name} value={ch.name}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label={ch.type} size="small" sx={{ minWidth: 60, fontSize: '0.7rem' }} />
                                <Box>
                                  <Typography variant="body2">{ch.displayName}</Typography>
                                  {ch.email && (
                                    <Typography variant="caption" color="text.secondary">
                                      {ch.email}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Grid container spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <Grid item xs>
                          <TextField
                            label="Add new email"
                            value={editPolicyForm.newEmailChannel}
                            onChange={(e) => handleEditPolicyFormChange('newEmailChannel', e.target.value)}
                            type="email"
                            placeholder="email@example.com"
                            fullWidth
                            size="small"
                            disabled={addingEmailChannel}
                          />
                        </Grid>
                        <Grid item>
                          <Button
                            variant="outlined"
                            onClick={handleAddEmailChannelToEdit}
                            disabled={
                              !editPolicyForm.newEmailChannel ||
                              !editPolicyForm.newEmailChannel.includes('@') ||
                              addingEmailChannel
                            }
                            sx={{ height: 40, minWidth: 70 }}
                          >
                            {addingEmailChannel ? <CircularProgress size={18} /> : 'Add'}
                          </Button>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setEditPolicyOpen(false);
                  setEditingPolicy(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePolicyEdits}
                variant="contained"
                disabled={
                  updatingPolicy ||
                  !editPolicyForm.displayName ||
                  !editPolicyForm.customQuery ||
                  !editPolicyForm.threshold ||
                  !editPolicyForm.duration ||
                  (editPolicyForm.conditionType !== 'threshold' && !editPolicyForm.evaluationInterval)
                }
              >
                {updatingPolicy ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={!!subscribingPolicy} onClose={() => setSubscribingPolicy(null)} maxWidth="sm" fullWidth>
            <DialogTitle>Subscribe to Policy by Email</DialogTitle>
            <DialogContent>
              {subscribingPolicy && (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>
                    Policy: {subscribingPolicy.displayName}
                  </Typography>
                  <Typography sx={{ mb: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                    Enter your email address to receive notifications when this policy triggers.
                  </Typography>
                  <TextField
                    label="Email Address"
                    type="email"
                    fullWidth
                    value={subscribeEmail}
                    onChange={(e) => {
                      setSubscribeEmail(e.target.value);
                      setSubscribeError(null);
                    }}
                    error={!!subscribeError}
                    helperText={subscribeError || 'You will receive email notifications when alerts fire for this policy'}
                    disabled={subscribing}
                    autoFocus
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSubscribingPolicy(null)} disabled={subscribing}>
                Cancel
              </Button>
              <Button onClick={handleSubscribe} variant="contained" disabled={!subscribeEmail || subscribing}>
                {subscribing ? <CircularProgress size={20} /> : 'Subscribe'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Create Policy Dialog */}
      <Dialog
        open={createPolicyOpen}
        onClose={() => {
          if (!createBusy) setCreatePolicyOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography component="span" variant="h6" sx={{ fontWeight: 600, display: 'block' }}>
              Create policy
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {createMode === 'simple'
                ? 'Pick what to watch. We apply recommended thresholds.'
                : 'Build a policy from a component template or a custom PromQL query.'}
            </Typography>
          </Box>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={createMode}
            disabled={createBusy}
            aria-label="Creation mode"
            onChange={(_e, v) => v && setCreateMode(v)}
          >
            <ToggleButton value="simple">Simple</ToggleButton>
            <ToggleButton value="advanced">Advanced</ToggleButton>
          </ToggleButtonGroup>
        </DialogTitle>
        {createMode === 'simple' ? (
          <SimplePolicySetup
            env={env}
            isEnvUser={isEnvUser}
            projects={creatableProjects}
            policyTemplates={policyTemplates}
            templatesLoading={templatesLoading}
            policies={policies}
            policiesReady={!!policiesData && !policiesLoading && !policiesError}
            policiesError={policiesError}
            getPolicyOwner={getPolicyOwner}
            getPolicyEnv={getPolicyEnv}
            emailChannels={emailNotificationChannels}
            channelsLoading={channelsLoading}
            onCancel={() => setCreatePolicyOpen(false)}
            onBusyChange={setCreateBusy}
            onChanged={applySimplePolicyChanges}
            onDone={() => setCreatePolicyOpen(false)}
          />
        ) : (
        <>
        <DialogContent dividers>
          {templatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              {createPolicyError && (
                <Alert severity="error" onClose={() => setCreatePolicyError(null)}>
                  {createPolicyError}
                </Alert>
              )}

              {/* Component Selection */}
              <FormControl fullWidth required>
                <InputLabel>Component</InputLabel>
                <Select
                  inputProps={{ id: 'tour-create-component' }}
                  value={createPolicyForm.component}
                  label="Component"
                  onChange={(e) => handleCreateFormChange('component', e.target.value)}
                >
                  {Object.keys(policyTemplates).map((comp) => (
                    <MenuItem key={comp} value={comp}>
                      {COMPONENT_DISPLAY_NAMES[comp] || comp}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {isEnvUser && (
                <FormControl fullWidth required>
                  <InputLabel>Policy Owner</InputLabel>
                  <Select
                    value={createPolicyForm.owner}
                    label="Policy Owner"
                    onChange={(e) => handleCreateFormChange('owner', e.target.value)}
                  >
                    <MenuItem value="system">System (Environment)</MenuItem>
                    <MenuItem value="project">Project</MenuItem>
                  </Select>
                </FormControl>
              )}

              {/* Template Selection */}
              {createPolicyForm.component && !createPolicyForm.useCustomQuery && (
                <FormControl fullWidth required>
                  <InputLabel>Alert Template</InputLabel>
                  <Select
                    inputProps={{ id: 'tour-create-template' }}
                    value={createPolicyForm.templateId}
                    label="Alert Template"
                    onChange={(e) => handleCreateFormChange('templateId', e.target.value)}
                  >
                    {(policyTemplates[createPolicyForm.component] || []).map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{t.description}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Template Info */}
              {createPolicyForm.templateId && !createPolicyForm.useCustomQuery && (() => {
                const template = (policyTemplates[createPolicyForm.component] || []).find((t) => t.id === createPolicyForm.templateId);
                const displayQuery =
                  createPolicyForm.component === 'network_manager'
                    ? getNetworkManagerTemplateQuery(template?.defaultQuery, createPolicyForm.owner)
                    : template?.defaultQuery;
                return template ? (
                  <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" gutterBottom>Template Details</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Chip label={`Severity: ${template.severity}`} size="small" sx={{ bgcolor: severityColors[template.severity]?.bg, color: severityColors[template.severity]?.color }} />
                      <Chip label={`Type: ${template.alertType}`} size="small" />
                    </Box>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', bgcolor: 'background.paper', p: 1, borderRadius: 1 }}>
                      {displayQuery}
                    </Typography>
                  </Paper>
                ) : null;
              })()}

              {/* Use Custom Query Toggle */}
              {createPolicyForm.component && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Switch
                    checked={createPolicyForm.useCustomQuery}
                    onChange={(e) => handleCreateFormChange('useCustomQuery', e.target.checked)}
                  />
                  <Typography variant="body2">Use custom PromQL query</Typography>
                </Box>
              )}

              {/* Custom Query */}
              {createPolicyForm.useCustomQuery && (
                <TextField
                  label="Custom PromQL Query"
                  value={createPolicyForm.customQuery}
                  onChange={(e) => handleCreateFormChange('customQuery', e.target.value)}
                  multiline
                  rows={4}
                  fullWidth
                  required
                  placeholder="Enter your PromQL query..."
                  sx={{ fontFamily: 'monospace' }}
                />
              )}

              {/* Configuration Parameters */}
              {(createPolicyForm.templateId || createPolicyForm.useCustomQuery) && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>Configuration</Typography>
                  
                  <TextField
                    label="Custom Display Name (optional)"
                    value={createPolicyForm.displayName}
                    onChange={(e) => handleCreateFormChange('displayName', e.target.value)}
                    fullWidth
                    placeholder="Leave blank to auto-generate"
                    helperText="Auto-generated: [Env] [Component] [Alert Name]"
                  />

                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Severity</InputLabel>
                    <Select
                      value={createPolicyForm.severity || 'warning'}
                      label="Severity"
                      onChange={(e) => handleCreateFormChange('severity', e.target.value)}
                    >
                      <MenuItem value="critical">critical</MenuItem>
                      <MenuItem value="warning">warning</MenuItem>
                      <MenuItem value="info">info</MenuItem>
                    </Select>
                  </FormControl>

                  {!createPolicyForm.useCustomQuery && selectedCreateTemplate?.params?.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Template Parameters</Typography>
                      <Grid container spacing={2}>
                        {selectedCreateTemplate.params.map((p) => (
                          <Grid key={p.key} item xs={12} sm={6}>
                            {p.type === 'select' && Array.isArray(p.options) ? (
                              <FormControl fullWidth>
                                <InputLabel>{p.label || p.key}</InputLabel>
                                <Select
                                  value={(createPolicyForm.templateParams || {})[p.key] ?? p.default ?? ''}
                                  label={p.label || p.key}
                                  onChange={(e) => handleCreateTemplateParamChange(p.key, e.target.value)}
                                >
                                  {p.options.map((opt) => (
                                    <MenuItem key={String(opt)} value={opt}>
                                      {String(opt)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <TextField
                                label={p.label || p.key}
                                value={(createPolicyForm.templateParams || {})[p.key] ?? ''}
                                onChange={(e) => handleCreateTemplateParamChange(p.key, e.target.value)}
                                type={p.type === 'number' ? 'number' : 'text'}
                                fullWidth
                                helperText={p.helperText || ''}
                                inputProps={{
                                  ...(p.type === 'number' ? {
                                    min: p.min,
                                    max: p.max,
                                    step: p.integer ? 1 : undefined,
                                  } : {}),
                                }}
                              />
                            )}
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Threshold"
                        value={createPolicyForm.threshold}
                        onChange={(e) => handleCreateFormChange('threshold', e.target.value)}
                        type="number"
                        fullWidth
                        required
                        helperText="Alert triggers when metric exceeds this value"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Duration"
                        value={createPolicyForm.duration}
                        onChange={(e) => handleCreateFormChange('duration', e.target.value)}
                        fullWidth
                        placeholder="300s"
                        helperText="How long condition must be true (e.g., 300s, 5m)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Evaluation Interval"
                        value={createPolicyForm.evaluationInterval}
                        onChange={(e) => handleCreateFormChange('evaluationInterval', e.target.value)}
                        fullWidth
                        placeholder="60s"
                        helperText="How often to check (e.g., 60s, 1m)"
                      />
                    </Grid>
                  </Grid>

                  <TextField
                    label="Auto Close Duration"
                    value={createPolicyForm.autoClose}
                    onChange={(e) => handleCreateFormChange('autoClose', e.target.value)}
                    fullWidth
                    placeholder="7d"
                    helperText="How long before alert auto-closes (e.g., 30m, 6h, 7d)"
                    sx={{ mt: 2 }}
                  />

                  {!isEnvUser || createPolicyForm.owner === 'project' ? (
                    isEnvUser ? (
                      <Autocomplete
                        freeSolo
                        options={creatableProjects}
                        value={createPolicyForm.projectId || ''}
                        onChange={(_event, newValue) => {
                          handleCreateFormChange('projectId', newValue || '');
                        }}
                        onInputChange={(_event, newInputValue) => {
                          handleCreateFormChange('projectId', newInputValue || '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Project"
                            helperText="Select from list or type a project id"
                          />
                        )}
                      />
                    ) : (
                      <FormControl fullWidth>
                        <InputLabel>Project</InputLabel>
                        <Select
                          label="Project"
                          value={createPolicyForm.projectId}
                          onChange={(e) => handleCreateFormChange('projectId', e.target.value)}
                        >
                          {creatableProjects.map((pid) => (
                            <MenuItem key={pid} value={pid}>
                              {pid}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Policy will be scoped to the selected project only.
                        </Typography>
                      </FormControl>
                    )
                  ) : (
                    <TextField
                      label="Project ID Pattern"
                      value={createPolicyForm.projectPattern}
                      onChange={(e) => handleCreateFormChange('projectPattern', e.target.value)}
                      fullWidth
                      placeholder="my-project-.*"
                      helperText="Regex pattern to match project IDs (e.g., my-project-.* for all environments)"
                    />
                  )}

                  {/* Notification Channels */}
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Notification Channels</Typography>
                  
                  {channelsLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">Loading channels...</Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Select existing channels */}
                      <FormControl fullWidth>
                        <InputLabel>Select Notification Channels</InputLabel>
                        <Select
                          multiple
                          open={createChannelsMenuOpen}
                          onOpen={() => setCreateChannelsMenuOpen(true)}
                          onClose={() => setCreateChannelsMenuOpen(false)}
                          value={createPolicyForm.selectedChannels}
                          label="Select Notification Channels"
                          onChange={(e) => handleCreateFormChange('selectedChannels', e.target.value)}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((channelName) => {
                                const channel = notificationChannels.find((c) => c.name === channelName);
                                return (
                                  <Chip
                                    key={channelName}
                                    label={channel?.displayName || channel?.email || channelName.split('/').pop()}
                                    size="small"
                                  />
                                );
                              })}
                            </Box>
                          )}
                        >
                          <ListSubheader
                            sx={{
                              position: 'sticky',
                              top: 0,
                              zIndex: 1,
                              bgcolor: 'background.paper',
                              display: 'flex',
                              justifyContent: 'flex-end',
                              py: 1,
                            }}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setCreateChannelsMenuOpen(false);
                              }}
                            >
                              Done
                            </Button>
                          </ListSubheader>
                          {emailNotificationChannels.map((ch) => (
                            <MenuItem key={ch.name} value={ch.name}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={ch.type}
                                  size="small"
                                  sx={{ minWidth: 60, fontSize: '0.7rem' }}
                                />
                                <Box>
                                  <Typography variant="body2">{ch.displayName}</Typography>
                                  {ch.email && (
                                    <Typography variant="caption" color="text.secondary">{ch.email}</Typography>
                                  )}
                                </Box>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Add new email channel */}
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs>
                          <TextField
                            label="Add new email"
                            value={createPolicyForm.newEmailChannel}
                            onChange={(e) => handleCreateFormChange('newEmailChannel', e.target.value)}
                            type="email"
                            placeholder="email@example.com"
                            fullWidth
                            size="small"
                            disabled={addingEmailChannel}
                          />
                        </Grid>
                        <Grid item>
                          <Button
                            variant="outlined"
                            onClick={handleAddEmailChannel}
                            disabled={!createPolicyForm.newEmailChannel || !createPolicyForm.newEmailChannel.includes('@') || addingEmailChannel}
                            sx={{ height: 40, minWidth: 70 }}
                          >
                            {addingEmailChannel ? <CircularProgress size={18} /> : 'Add'}
                          </Button>
                        </Grid>
                      </Grid>

                      {/* Show selected channels summary */}
                      {createPolicyForm.selectedChannels.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {createPolicyForm.selectedChannels.length} notification channel(s) selected
                        </Typography>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatePolicyOpen(false)} disabled={creatingPolicy}>
            Cancel
          </Button>
          <Button
            onClick={handleCreatePolicy}
            variant="contained"
            disabled={
              creatingPolicy ||
              !createPolicyForm.component ||
              (!createPolicyForm.templateId && !createPolicyForm.useCustomQuery) ||
              (createPolicyForm.useCustomQuery && !createPolicyForm.customQuery) ||
              !createPolicyForm.threshold
            }
            id="tour-create-submit"
          >
            {creatingPolicy ? <CircularProgress size={20} /> : 'Create Policy'}
          </Button>
        </DialogActions>
        </>
        )}
      </Dialog>

      {/* Guided Tour Dialog */}
      <Dialog open={tourOpen} onClose={() => setTourOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutlineIcon fontSize="small" />
          Guided Policy Tour
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip
              label="LMS policy tour"
              clickable
              color={tourMode === 'lms' ? 'primary' : 'default'}
              onClick={() => startTour('lms')}
              variant={tourMode === 'lms' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 700 }}
            />
            <Chip
              label="Network Manager policy tour"
              clickable
              color={tourMode === 'network_manager' ? 'primary' : 'default'}
              onClick={() => startTour('network_manager')}
              variant={tourMode === 'network_manager' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 700 }}
            />
          </Box>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
            {tourSteps[tourStep]?.title || 'Tour'}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            {tourSteps[tourStep]?.body || ''}
          </Typography>
          <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: '0.8rem' }}>
            Step {Math.min(tourStep + 1, tourSteps.length)} of {tourSteps.length}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTourOpen(false)}>Close</Button>
          <Button onClick={() => setTourStep((s) => Math.max(0, s - 1))} disabled={tourStep <= 0}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={() => setTourStep((s) => Math.min(tourSteps.length - 1, s + 1))}
            disabled={tourStep >= tourSteps.length - 1}
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonitoringDashboard;


